import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { openEditor } from '@/lib/openEditor';
import { CHURCHCONNECT_SUBDOMAIN_BASE, EASY_PEAZY_SUBDOMAIN_BASE } from '@/lib/subdomainBase';
import { handoverInvitePreview, handoverInviteeName, PERSONAL_NOTE_MAX } from '@/lib/handoverInviteCopy';
import { Globe, LayoutDashboard, Users, Server, Headphones, CreditCard, BarChart3, Settings, LogOut, DollarSign, TrendingUp, HardDrive, ChevronRight, Save, Eye, Send, X, Gift, ExternalLink, RefreshCw, Mail, CheckCircle, Clock, AlertCircle, Tag, Plus, Trash2, ToggleLeft, ToggleRight, Download, ArrowRightLeft, ShoppingBag, Image as ImageIcon, Search, EyeOff } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import BringOverChoice from '@/components/BringOverChoice';
import AdminCreateSiteCard from '@/components/AdminCreateSiteCard';
import { WebsitePeople } from '@/components/WebsitePeople';
import AdminSharedDomains from '@/components/AdminSharedDomains';
import WebsiteReadyActions from '@/components/WebsiteReadyActions';
import { IMPROVE } from '@/lib/importFinishCopy';

const tabs = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'websites', label: 'Websites & Domains', icon: Server },
  { id: 'shared-domains', label: 'Shared Domains', icon: Globe },
  { id: 'import', label: 'Bring a Site Over', icon: ArrowRightLeft },
  { id: 'emails', label: 'Email Tasks', icon: Mail },
  { id: 'coupons', label: 'Coupons', icon: Tag },
  { id: 'referrals', label: 'Referrals', icon: Gift },
  { id: 'support', label: 'Support Tickets', icon: Headphones },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'settings', label: 'Integrations', icon: Settings },
];

function statusLabel(status) {
  if (status === 'quarantined') return 'Hidden (URL kept)';
  if (status === 'reserved') return 'Reserved URL';
  return status;
}

function StatusBadge({ status }) {
  const cls = { active: 'badge-active', pending: 'badge-pending', provisioning: 'badge-pending', open: 'badge-info', in_progress: 'badge-pending', resolved: 'badge-active', closed: 'badge-error', completed: 'badge-active', cancelled: 'badge-error', quarantined: 'badge-error', reserved: 'badge-info' }[status] || 'badge-info';
  return <span className={cls}>{statusLabel(status)}</span>;
}

function StatusChip({ children, tone = 'slate', testId }) {
  const tones = {
    slate: 'bg-slate-800 text-slate-300',
    ok: 'bg-emerald-500/15 text-emerald-300',
    warn: 'bg-amber-500/15 text-amber-300',
    bad: 'bg-rose-500/15 text-rose-300',
    info: 'bg-indigo-500/15 text-indigo-300',
  };
  return <span data-testid={testId} className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${tones[tone] || tones.slate}`}>{children}</span>;
}

// Continuity glue only. Counts and sorts use the already-loaded admin payload.
// Do not invent traffic, SEO, storage meters, or Stripe invoice sync here.
function moneyAmount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  const n = moneyAmount(value);
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
}

function recordedPaymentsTotal(payments) {
  return (payments || []).reduce((sum, p) => sum + moneyAmount(p?.amount), 0);
}

function planFeeOf(sub) {
  return moneyAmount(sub?.monthly_fee);
}

function isHiddenWebsite(website) {
  return !!(website?.quarantined || website?.status === 'quarantined');
}

function isErrorWebsite(website) {
  return website?.status === 'error' || website?.status === 'failed';
}

function isLiveWebsite(website) {
  return !isHiddenWebsite(website) && !isErrorWebsite(website);
}

function timeMs(value) {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

function compareTimeDesc(a, b) {
  const am = timeMs(a);
  const bm = timeMs(b);
  if (am == null && bm == null) return 0;
  if (am == null) return 1;
  if (bm == null) return -1;
  return bm - am;
}

function compareTimeNeverFirst(a, b) {
  const am = timeMs(a);
  const bm = timeMs(b);
  if (am == null && bm == null) return 0;
  if (am == null) return -1;
  if (bm == null) return 1;
  return am - bm;
}

function neverSignedIn(row) {
  return !row?.ever_accessed && !row?.last_login;
}

function signInLabel(row) {
  return row?.last_login || row?.ever_accessed ? formatWhen(row.last_login) : 'Never';
}

function scrollToDesk(id) {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function DeskCard({ label, value, hint, active, onClick, testId, icon: Icon, color, bg }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!active}
      className={`w-full text-left rounded-xl border p-4 flex items-center gap-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 ${
        active
          ? 'border-indigo-500/40 bg-indigo-500/10'
          : 'border-slate-800 bg-slate-900/40 hover:border-indigo-500/30 hover:bg-slate-900/70'
      }`}
      data-testid={testId}
    >
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className={`text-xl font-bold ${color} truncate`} style={{ fontFamily: 'Outfit' }}>{value}</p>
        {hint ? <p className="text-[11px] text-slate-600 mt-0.5">{hint}</p> : null}
      </div>
    </button>
  );
}

function DeskSort({ value, onChange, options, testId, label = 'Sort' }) {
  return (
    <label className="text-xs text-slate-400 flex items-center gap-2">
      {label}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="bg-slate-900 border border-slate-700 rounded-lg h-9 px-2 text-sm text-white"
        data-testid={testId}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </label>
  );
}

export default function AdminDashboard() {
  const { user, signOutEverywhere } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const startBringOver = searchParams.get('bring') === '1' || requestedTab === 'import';
  const activeTab = requestedTab === 'import'
    ? 'websites'
    : (tabs.some(t => t.id === requestedTab) ? requestedTab : 'overview');
  const openTaskId = searchParams.get('task') || '';

  const openTab = useCallback((tab, taskId) => {
    const next = new URLSearchParams();
    if (tab === 'import') {
      next.set('tab', 'websites');
      next.set('bring', '1');
    } else if (tab && tab !== 'overview') {
      next.set('tab', tab);
    }
    if (tab === 'emails' && taskId) next.set('task', taskId);
    setSearchParams(next);
  }, [setSearchParams]);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-dashboard">
      <aside className="h-screen bg-slate-950 border-r border-slate-800 w-64 fixed left-0 top-0 flex flex-col z-40" data-testid="admin-sidebar">
        <div className="h-16 flex items-center px-6 border-b border-slate-800">
          <Globe className="w-6 h-6 text-indigo-400 mr-2" />
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
          <span className="ml-2 text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded">Admin</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => openTab(t.id)} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${activeTab === t.id ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`} data-testid={`admin-tab-${t.id}`}>
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <div className="truncate">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.full_name || 'Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button type="button" onClick={() => signOutEverywhere()} className="text-slate-500 hover:text-red-400 transition-colors" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </aside>

      <main className="ml-64 w-[calc(100%-16rem)] min-w-0 max-w-full p-8">
        {activeTab === 'overview' && <AdminOverview onOpenTab={openTab} />}
        {activeTab === 'customers' && <AdminCustomers />}
        {activeTab === 'websites' && <AdminWebsites startBringOver={startBringOver} />}
        {activeTab === 'shared-domains' && <AdminSharedDomains />}
        {activeTab === 'emails' && <AdminEmailTasks openTaskId={openTaskId} onOpenTask={(taskId) => openTab('emails', taskId)} onClearTask={() => openTab('emails')} />}
        {activeTab === 'coupons' && <AdminCoupons />}
        {activeTab === 'referrals' && <AdminReferrals />}
        {activeTab === 'support' && <AdminSupport />}
        {activeTab === 'payments' && <AdminPayments />}
        {activeTab === 'analytics' && <AdminAnalytics />}
        {activeTab === 'settings' && <AdminSettings />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bring a site over.
//
// Two buttons, because the two halves have different deadlines. Reading a site
// races a hosting cancellation and emails nobody. Building creates the redesign
// so you can work on it. Invite is off unless asked. Transfer hands it over.
// ---------------------------------------------------------------------------
function importError(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg || item).filter(Boolean).join(' ');
  }
  return e?.message || fallback;
}

function AdminSiteImport({ embedded = false, onBuilt } = {}) {
  const { user } = useAuth();
  const [sourceUrl, setSourceUrl] = useState('');
  const [capture, setCapture] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [reading, setReading] = useState(false);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [owner, setOwner] = useState({ owner_email: '', owner_name: '', label: '', business_type: '' });
  const [sendInvite, setSendInvite] = useState(false);
  const [finish, setFinish] = useState(IMPROVE);
  const [finishOptions, setFinishOptions] = useState(null);
  const [watching, setWatching] = useState(false);

  const loadCaptures = useCallback(() => {
    api.listSiteCaptures().then(r => setCaptures(r.data.captures || [])).catch(() => {});
  }, []);
  useEffect(() => { loadCaptures(); }, [loadCaptures]);
  useEffect(() => {
    api.siteImportOptions().then(r => setFinishOptions(r.data.finish || null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.email) {
      setOwner((o) => (o.owner_email ? o : { ...o, owner_email: user.email }));
    }
  }, [user?.email]);
  useEffect(() => {
    if (!result) return;
    const el = typeof document !== 'undefined' ? document.querySelector('[data-testid="import-result"]') : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [result]);

  const suggestLabel = (host) => (host || '').replace(/^www\./, '').split('.')[0];

  const read = async () => {
    setReading(true); setError(''); setResult(null);
    try {
      const r = await api.captureSite({ source_url: sourceUrl });
      setCapture(r.data.capture);
      setOwner(o => ({ ...o, label: o.label || suggestLabel(new URL(r.data.capture.source_url).hostname) }));
      loadCaptures();
    } catch (e) {
      setError(importError(e, 'Could not read that site.'));
    } finally { setReading(false); }
  };

  const build = async () => {
    const workingEmail = (owner.owner_email || user?.email || '').trim();
    const workingLabel = (owner.label || '').trim();
    if (!workingLabel) {
      setError('Add a web address label so the redesign has a home (label.easypeazy.site).');
      return;
    }
    if (!workingEmail) {
      setError('Your admin email is missing, so there is no account to hold the site while you work on it.');
      return;
    }
    setBuilding(true); setError(''); setResult(null); setWatching(false);
    try {
      const r = await api.buildImportedSite({
        source_url: capture.source_url,
        finish,
        owner_email: workingEmail,
        owner_name: owner.owner_name,
        label: workingLabel,
        business_type: owner.business_type,
        send_invite: Boolean(sendInvite),
      });
      setResult(r.data);
      setBuilding(false);
      if (typeof onBuilt === 'function' && r.data?.website) onBuilt(r.data);
      const id = r.data?.website?.id;
      const alreadyReady = r.data?.address_live || r.data?.website?.status === 'active';
      if (id && !alreadyReady && r.data?.website?.status !== 'error' && r.data?.website?.status !== 'failed') {
        setWatching(true);
        const started = Date.now();
        while (Date.now() - started < 120000) {
          await new Promise((res) => setTimeout(res, 3000));
          try {
            const tick = await api.getAdminWebsiteStatus(id);
            const site = tick.data || {};
            setResult((prev) => prev ? {
              ...prev,
              website: { ...prev.website, ...site },
              address_live: site.status === 'active',
            } : prev);
            if (site.status === 'active' || site.status === 'error' || site.status === 'failed') break;
          } catch (_) { /* keep waiting */ }
        }
        setWatching(false);
      }
    } catch (e) {
      setError(importError(e, 'The site could not be built.'));
      setBuilding(false);
    }
  };

  return (
    <div data-testid="admin-site-import">
      {embedded ? (
        <p className="text-sm text-slate-400 mb-4">
          Paste their current address. We read it, then you redesign it here. Nobody is emailed unless you ask.
        </p>
      ) : (
        <>
          <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Bring a Site Over</h1>
          <p className="text-slate-400 mb-6 text-sm max-w-3xl">
            Read their current site, then redesign it so you can work on it. Nobody is emailed
            unless you ask. Transfer on Websites &amp; Domains when they should own it.
          </p>
        </>
      )}

      <div className="card p-5 mb-6">
        <label className="block text-sm text-slate-300 mb-2">Their current website address</label>
        <div className="flex gap-3">
          <input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)}
            placeholder="https://www.theirsite.com"
            className="flex-1 bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 placeholder:text-slate-400 outline-none"
            data-testid="import-url-input" />
          <button onClick={read} disabled={reading || !sourceUrl.trim()} className="btn-primary flex items-center gap-2" data-testid="import-read-btn">
            {reading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {reading ? 'Reading…' : 'Read this site'}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Nothing is built and nobody is emailed by reading.</p>
      </div>

      {error && <div className="card p-4 mb-6 border border-red-500/30 text-red-300 text-sm" data-testid="import-error">{error}</div>}

      {capture && (
        <div className="card p-5 mb-6" data-testid="import-capture">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{capture.site_name || capture.source_url}</h2>
            <span className="text-xs text-slate-500">{capture.source_url}</span>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-5">
            {[['Pages read', capture.page_count, Eye],
              ['Products found', capture.product_count, ShoppingBag],
              ['Photographs', capture.photo_count, ImageIcon],
              ['Payment links', (capture.payment_links || []).length, CreditCard]].map(([label, value, Icon]) => (
              <div key={label} className="bg-slate-900/60 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Icon className="w-3.5 h-3.5" />{label}</div>
                <div className="text-xl font-semibold text-white">{value}</div>
              </div>
            ))}
          </div>
          {capture.photo_count > 0 && (
            <p className="text-xs text-slate-500 -mt-3 mb-5">
              We copy every photograph into your editor library so you can place them anywhere.
            </p>
          )}

          {Object.keys(capture.facts || {}).length > 0 && (
            <div className="mb-5">
              <h3 className="text-sm font-medium text-slate-300 mb-2">Contact details their site publishes</h3>
              <div className="space-y-1">
                {Object.entries(capture.facts).map(([field, fact]) => (
                  <div key={field} className="flex items-center gap-3 text-sm">
                    <span className="text-slate-500 w-20 capitalize">{field}</span>
                    <span className="text-slate-200">{fact.value}</span>
                    <a href={fact.source} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-300 text-xs flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" />where this came from
                    </a>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Only these go onto the new site as facts. Nothing else about them is stated — anything
                we did not find is left out rather than made up.
              </p>
            </div>
          )}

          {(capture.products || []).length > 0 && (
            <div className="mb-5">
              <h3 className="text-sm font-medium text-slate-300 mb-2">What they sell</h3>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {capture.products.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-slate-900/40 rounded px-3 py-1.5">
                    <span className="text-slate-200 truncate">{p.name}</span>
                    <span className="text-slate-400 ml-4 shrink-0">{p.price || '—'}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Prices are copied exactly as they wrote them. The new site lists these with a Buy button
                pointing at the payment link they already use — it is not a checkout, and no money passes
                through us.
              </p>
            </div>
          )}

          {(capture.payment_links || []).length > 0 && (
            <div className="mb-5 text-sm">
              <h3 className="text-sm font-medium text-slate-300 mb-2">Where they already take money</h3>
              {capture.payment_links.map((l, i) => (
                <div key={i} className="text-slate-300">{l.kind} — <span className="text-slate-500">{l.url}</span></div>
              ))}
            </div>
          )}

          <div className="border-t border-slate-800 pt-5 mb-5">
            <BringOverChoice
              finish={finish}
              onChange={setFinish}
              keepAvailable={!!(finishOptions?.keep?.available || capture.finish?.keep?.available)}
              sourceUrl={capture.source_url}
              capture={capture}
              testIdPrefix="import"
            />
          </div>

          <div className="border-t border-slate-800 pt-5">
            <h3 className="text-sm font-medium text-slate-300 mb-1">Build it so you can work on it</h3>
            <p className="text-xs text-slate-500 mb-3">
              The choice above is Keep vs Redesign — it does not start the build.
              Press the button here. The site stays on your desk until you Transfer it.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input value={owner.owner_email} onChange={e => setOwner({ ...owner, owner_email: e.target.value })}
                placeholder="Your email while you work (not theirs yet)"
                className="bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 placeholder:text-slate-400 outline-none"
                data-testid="import-owner-email" />
              <input value={owner.owner_name} onChange={e => setOwner({ ...owner, owner_name: e.target.value })}
                placeholder="Name (optional)"
                className="bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 placeholder:text-slate-400 outline-none" />
              <input value={owner.label} onChange={e => setOwner({ ...owner, label: e.target.value })}
                placeholder="web address label"
                className="bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 placeholder:text-slate-400 outline-none"
                data-testid="import-label" />
              <input value={owner.business_type} onChange={e => setOwner({ ...owner, business_type: e.target.value })}
                placeholder="Kind of business (optional)"
                className="bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 placeholder:text-slate-400 outline-none" />
            </div>
            <p className="text-xs text-slate-500 mb-3">
              It will live at <span className="text-slate-300">{owner.label || 'label'}.{EASY_PEAZY_SUBDOMAIN_BASE}</span> while you work.
              That address is pointed during Build. Transfer on Websites &amp; Domains when they should own it.
            </p>
            <label className="flex items-center gap-2 text-xs text-slate-400 mb-3">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                data-testid="import-send-invite"
              />
              Email them the keys now (skip if you will Transfer later)
            </label>
            <button
              type="button"
              onClick={build}
              disabled={building || !(owner.label || '').trim() || (finish === 'keep' && !(finishOptions?.keep?.available || capture.finish?.keep?.available))}
              className="btn-primary"
              data-testid="import-build-btn"
            >
              {building ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
              {building ? 'Building the redesign…' : finish === 'keep' ? 'Keep this layout so I can work on it' : 'Redesign it so I can work on it'}
            </button>
            {!(owner.label || '').trim() && !building ? (
              <p className="text-xs text-amber-300 mt-2" data-testid="import-build-needed">Add a web address label, then press the button. Choosing Redesign above is not enough.</p>
            ) : null}
            {building && <p className="text-xs text-slate-500 mt-2">This takes about a minute and a half. Stay on this page.</p>}
          </div>
        </div>
      )}

      {result && (
        <div className={`card p-6 mb-6 ${result.address_live || result.website?.status === 'active' ? 'border border-emerald-500/40 bg-emerald-500/5' : result.website?.status === 'error' || result.website?.status === 'failed' ? 'border border-red-500/40' : 'border border-amber-500/30'}`} data-testid="import-result">
          <div className="flex items-start gap-3 mb-4">
            {result.address_live || result.website?.status === 'active'
              ? <CheckCircle className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
              : watching || result.website?.status === 'provisioning'
                ? <RefreshCw className="w-6 h-6 text-amber-400 shrink-0 mt-0.5 animate-spin" />
                : <AlertCircle className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />}
            <div>
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit' }} data-testid="import-result-title">
                {result.website?.status === 'error' || result.website?.status === 'failed'
                  ? `${result.website.site_name || 'The site'} did not finish building`
                  : result.address_live || result.website?.status === 'active'
                    ? `${result.website.site_name || 'Your site'} is ready`
                    : watching
                      ? `Publishing ${result.website.site_name || 'your site'}…`
                      : `${result.website.site_name || 'Your site'} is built — waiting for the address`}
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                {result.address_live || result.website?.status === 'active'
                  ? 'Open it to look, or edit it. You are already signed in here — editing does not ask for another password.'
                  : watching
                    ? 'Stay here. We will turn on Open website when the address answers.'
                    : 'You can still try Open website or Edit this website. If a page does not load yet, wait a moment and try again.'}
              </p>
            </div>
          </div>
          <WebsiteReadyActions website={result.website} size="lg" testIdPrefix="import" />
          <p className="text-xs text-slate-500 mt-4">
            There is no separate login on the live site. Edit this website is the owner area.
            The customer gets a login after you Transfer on Websites &amp; Domains.
          </p>
          <p className="text-xs text-slate-500 mt-1" data-testid="import-result-url">{result.website?.site_url}</p>
          {(result.notes || []).length > 0 && (
            <ul className="mt-4 space-y-1">
              {result.notes.map((n, i) => (
                <li key={i} className="text-amber-300/90 text-xs flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{n}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {captures.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-200">Sites already read</h2>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Kept whether or not they were built. Once their old host is switched off, this is the only copy.
          </p>
          <div className="space-y-1">
            {captures.map((c, i) => (
              <button key={i} onClick={() => { setCapture(c); setSourceUrl(c.source_url); setOwner(o => ({ ...o, label: o.label || suggestLabel(new URL(c.source_url).hostname) })); }}
                className="w-full flex items-center justify-between text-sm bg-slate-900/40 hover:bg-slate-900/70 rounded px-3 py-2 transition-colors text-left">
                <span className="text-slate-200 truncate">{c.site_name || c.source_url}</span>
                <span className="text-slate-500 text-xs ml-4 shrink-0">
                  {c.page_count} pages · {c.product_count} products
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminOverview({ onOpenTab }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.getAdminStats().then(r => setStats(r.data)).catch(console.error);
  }, []);

  if (!stats) return <div className="text-slate-500">Loading...</div>;

  // Every card opens the tab that explains the number on it. A number with no
  // way through to the rows behind it makes the owner read a figure and then go
  // hunting in the sidebar for what it was made of.
  const cards = [
    { label: 'Customers', value: stats.total_customers, icon: Users, color: 'text-indigo-400', bg: 'bg-indigo-500/10', tab: 'customers' },
    { label: 'Monthly Revenue', value: `$${stats.monthly_revenue}`, icon: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/10', tab: 'payments' },
    { label: 'Websites', value: stats.total_websites, icon: Server, color: 'text-cyan-400', bg: 'bg-cyan-500/10', tab: 'websites' },
    // Domains live on the Websites & Domains tab, so both cards land there.
    { label: 'Active Domains', value: stats.active_domains, icon: Globe, color: 'text-purple-400', bg: 'bg-purple-500/10', tab: 'websites' },
    { label: 'Open Tickets', value: stats.open_tickets, icon: Headphones, color: 'text-amber-400', bg: 'bg-amber-500/10', tab: 'support' },
    // storage_used_mb is inserted 0 and never collected. Do not show a GB figure.
    { label: 'Storage Used', value: 'Not metered', icon: HardDrive, color: 'text-teal-400', bg: 'bg-teal-500/10', tab: 'analytics' },
  ];

  const tabLabel = (id) => tabs.find(t => t.id === id)?.label || id;

  return (
    <div className="animate-fade-in" data-testid="admin-overview">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Admin Dashboard</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {cards.map((c, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onOpenTab(c.tab)}
            aria-label={`${c.label}: ${c.value}. Open ${tabLabel(c.tab)}`}
            className="group w-full text-left rounded-xl border border-slate-800 bg-slate-900/40 p-6 flex items-center gap-4 transition-colors hover:border-indigo-500/30 hover:bg-slate-900/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
            data-testid={`admin-stat-${i}`}
          >
            <div className={`w-12 h-12 rounded-xl ${c.bg} flex items-center justify-center`}>
              <c.icon className={`w-6 h-6 ${c.color}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">{c.label}</p>
              <p className={`text-2xl font-bold ${c.color}`} style={{ fontFamily: 'Outfit' }}>{c.value}</p>
            </div>
            <ChevronRight className="w-4 h-4 ml-auto text-slate-600 group-hover:text-indigo-400 transition-colors" />
          </button>
        ))}
      </div>

      <div className="flex justify-end mb-8">
        <div className="w-full max-w-md">
          <AdminCreateSiteCard onCreated={() => onOpenTab('websites')} />
        </div>
      </div>

      {/* Recent Payments */}
      {stats.recent_payments?.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Recent Payments</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Type</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Amount</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Covers</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_payments.map(p => (
                <tr key={p.id} className="border-b border-slate-800/50">
                  <td className="py-3 px-4 text-slate-300">{new Date(p.payment_date || p.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4 text-slate-300 capitalize">{p.payment_type}</td>
                  <td className="py-3 px-4 text-slate-300">${p.amount}</td>
                  <td className="py-3 px-4"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminCustomers() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(null);
  const [deleteNote, setDeleteNote] = useState('');
  const [resetBusy, setResetBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sortKey, setSortKey] = useState('created_at_desc');
  const [filterKey, setFilterKey] = useState('all');

  const reload = () => api.getAdminClients().then(r => { setData(r.data); setLoadError(null); }).catch(() => {
    setLoadError('Could not load customers. Check your connection and try again.');
  });

  useEffect(() => { reload(); }, []);

  const toggleFreeHosting = async (clientId) => {
    try {
      await api.toggleFreeHosting(clientId);
      const res = await api.getAdminClients();
      setData(res.data);
    } catch (err) { console.error(err); }
  };

  const handleDeleteCustomer = async (client) => {
    const live = (data.websites || []).filter(w => w.client_id === client.id && !w.quarantined && w.status !== 'quarantined');
    if (live.length) {
      setDeleteNote(`Transfer or Hide & keep URL on their website first (${live.map(w => w.site_name || w.site_url).join(', ')}).`);
      return;
    }
    if (!window.confirm(`Delete customer ${client.email || client.full_name}? Hidden sites keep their URL. This cannot be undone.`)) return;
    setDeleteBusy(client.id);
    setDeleteNote('');
    try {
      const res = await api.deleteAdminClient(client.id);
      const held = res.data.labels_held;
      setDeleteNote(held ? `Customer removed. ${held} URL(s) still held so they cannot be claimed.` : 'Customer removed.');
      if (openId === client.id) setOpenId(null);
      const refreshed = await api.getAdminClients();
      setData(refreshed.data);
    } catch (err) {
      setDeleteNote(adminActionError(err, 'Could not delete that customer.'));
    }
    setDeleteBusy(null);
  };

  const handleSendReset = async (client) => {
    if (!window.confirm(`Send a password reset to ${client.email}? They get a 1-hour link. This is help for a locked-out customer, not a new invite.`)) return;
    setResetBusy(client.id);
    setDeleteNote('');
    try {
      const res = await api.sendAdminPasswordReset(client.id);
      setDeleteNote(res.data.message || `Password reset sent to ${client.email}.`);
    } catch (err) {
      setDeleteNote(adminActionError(err, 'Could not send a password reset.'));
    }
    setResetBusy(null);
  };

  const handleBulkDeleteEmpty = async (count) => {
    if (!window.confirm(`Delete ${count} leftover customer${count === 1 ? '' : 's'} with no website? Accounts that still have a site are left alone. This cannot be undone.`)) return;
    setBulkBusy(true);
    setDeleteNote('');
    try {
      const res = await api.bulkDeleteEmptyClients();
      const skipped = res.data.skipped || 0;
      setDeleteNote(`Removed ${res.data.deleted || 0} leftover customer${(res.data.deleted || 0) === 1 ? '' : 's'} with no website.${skipped ? ` ${skipped} skipped.` : ''}`);
      const refreshed = await api.getAdminClients();
      setData(refreshed.data);
    } catch (err) {
      setDeleteNote(adminActionError(err, 'Could not delete leftover customers.'));
    }
    setBulkBusy(false);
  };

  if (loadError) return <div className="text-red-400">{loadError}</div>;
  if (!data) return <div className="text-slate-500">Loading...</div>;

  const getClientSub = (cid) => data.subscriptions?.find(s => s.client_id === cid);
  const getClientWebsites = (cid) => data.websites?.filter(w => w.client_id === cid) || [];
  const getClientPayments = (cid) => (data.payments || []).filter(p => p.client_id === cid);
  const getClientTx = (cid) => (data.payment_transactions || []).filter(p => p.client_id === cid);
  const emptyClients = (data.clients || []).filter(c => getClientWebsites(c.id).length === 0);
  const liveSites = (data.websites || []).filter(isLiveWebsite);
  const neverSignedInCount = (data.clients || []).filter(neverSignedIn).length;
  const recordedTotal = recordedPaymentsTotal(data.payments);
  const planFeeTotal = (data.subscriptions || []).reduce((sum, sub) => sum + planFeeOf(sub), 0);
  const toggleFilter = (key) => setFilterKey(current => current === key ? 'all' : key);

  const listedClients = [...(data.clients || [])]
    .filter((c) => {
      const sites = getClientWebsites(c.id);
      if (filterKey === 'never_signed_in') return neverSignedIn(c);
      if (filterKey === 'leftover') return sites.length === 0;
      if (filterKey === 'live_sites') return sites.some(isLiveWebsite);
      if (filterKey === 'payments') return recordedPaymentsTotal(getClientPayments(c.id)) > 0 || planFeeOf(getClientSub(c.id)) > 0;
      return true;
    })
    .sort((a, b) => {
      const aSites = getClientWebsites(a.id).filter(w => !isHiddenWebsite(w)).length;
      const bSites = getClientWebsites(b.id).filter(w => !isHiddenWebsite(w)).length;
      const aRev = recordedPaymentsTotal(getClientPayments(a.id));
      const bRev = recordedPaymentsTotal(getClientPayments(b.id));
      const aFee = planFeeOf(getClientSub(a.id));
      const bFee = planFeeOf(getClientSub(b.id));
      if (sortKey === 'created_at_asc') return (timeMs(a.created_at) || 0) - (timeMs(b.created_at) || 0);
      if (sortKey === 'website_count_desc') return bSites - aSites;
      if (sortKey === 'website_count_asc') return aSites - bSites;
      if (sortKey === 'last_login_desc') return compareTimeDesc(a.last_login, b.last_login);
      if (sortKey === 'last_login_never') return compareTimeNeverFirst(a.last_login, b.last_login);
      if (sortKey === 'revenue_desc') return (bRev + bFee) - (aRev + aFee);
      return (timeMs(b.created_at) || 0) - (timeMs(a.created_at) || 0);
    });

  return (
    <div className="animate-fade-in" data-testid="admin-customers">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Customer Management</h2>
        {emptyClients.length > 0 && (
          <button
            type="button"
            onClick={() => handleBulkDeleteEmpty(emptyClients.length)}
            disabled={bulkBusy}
            className="text-xs bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 px-3 py-1.5 rounded-lg disabled:opacity-50"
            data-testid="bulk-delete-empty-customers"
          >
            {bulkBusy ? 'Deleting…' : `Delete ${emptyClients.length} leftover customer${emptyClients.length === 1 ? '' : 's'} with no website`}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="customers-summary-cards">
        <DeskCard label="Customers" value={(data.clients || []).length} hint="From this list" active={filterKey === 'all'} onClick={() => setFilterKey('all')} testId="customers-card-customers" icon={Users} color="text-indigo-400" bg="bg-indigo-500/10" />
        <DeskCard label="Live sites" value={liveSites.length} hint="Not hidden" active={filterKey === 'live_sites'} onClick={() => toggleFilter('live_sites')} testId="customers-card-live-sites" icon={Server} color="text-cyan-400" bg="bg-cyan-500/10" />
        <DeskCard label="Never signed in" value={neverSignedInCount} hint="EasyPeazy sign-in" active={filterKey === 'never_signed_in'} onClick={() => toggleFilter('never_signed_in')} testId="customers-card-never-signed-in" icon={Clock} color="text-amber-400" bg="bg-amber-500/10" />
        <DeskCard label="0-site leftovers" value={emptyClients.length} hint="No website yet" active={filterKey === 'leftover'} onClick={() => toggleFilter('leftover')} testId="customers-card-leftovers" icon={Users} color="text-rose-300" bg="bg-rose-500/10" />
        <DeskCard label="Recorded / plan fee" value={`${formatMoney(recordedTotal)} / ${formatMoney(planFeeTotal)}/mo`} hint="ep_payments + plan fee" active={filterKey === 'payments'} onClick={() => toggleFilter('payments')} testId="customers-card-payments" icon={DollarSign} color="text-emerald-400" bg="bg-emerald-500/10" />
      </div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <DeskSort
          value={sortKey}
          onChange={setSortKey}
          testId="customers-sort"
          options={[
            { value: 'created_at_desc', label: 'Joined — newest' },
            { value: 'created_at_asc', label: 'Joined — oldest' },
            { value: 'website_count_desc', label: 'Website count — most' },
            { value: 'website_count_asc', label: 'Website count — fewest' },
            { value: 'last_login_desc', label: 'EasyPeazy sign-in — recent' },
            { value: 'last_login_never', label: 'EasyPeazy sign-in — never first' },
            { value: 'revenue_desc', label: 'Recorded revenue' },
          ]}
        />
        {filterKey !== 'all' && (
          <button type="button" onClick={() => setFilterKey('all')} className="text-xs text-indigo-400 hover:text-indigo-300" data-testid="customers-clear-filter">
            Show all {data.clients?.length || 0} customers
          </button>
        )}
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Customer</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Plan</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Websites</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Status</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Free Hosting</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Joined</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">EasyPeazy sign-in</th>
              <th className="text-right py-3 px-6 text-slate-400 font-medium"> </th>
            </tr>
          </thead>
          <tbody>
            {listedClients.length === 0 ? (
              <tr><td colSpan={8} className="py-8 text-center text-slate-500">No customers match this sort/filter.</td></tr>
            ) : listedClients.map(c => {
              const sub = getClientSub(c.id);
              const sites = getClientWebsites(c.id);
              return (
                <Fragment key={c.id}>
                <tr className="border-b border-slate-800/50 hover:bg-slate-800/20">
                  <td className="py-3 px-6">
                    <button
                      type="button"
                      onClick={() => setOpenId(openId === c.id ? null : c.id)}
                      className="text-slate-200 font-medium text-left hover:text-white hover:underline"
                    >
                      {c.full_name || 'Unnamed'}
                    </button>
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="text-xs text-sky-400 hover:underline">{c.email}</a>
                    ) : (
                      <p className="text-xs text-slate-500">No email</p>
                    )}
                    {c.phone ? (
                      <p className="text-xs text-slate-500 mt-0.5">
                        <a href={`tel:${c.phone}`} className="text-sky-400 hover:underline">{c.phone}</a>
                        {String(c.phone).replace(/[^\d+]/g, '') && (
                          <>
                            {' · '}
                            <a href={`sms:${String(c.phone).replace(/[^\d+]/g, '')}`} className="text-sky-400 hover:underline">Text</a>
                          </>
                        )}
                      </p>
                    ) : null}
                  </td>
                  <td className="py-3 px-6 text-slate-300 capitalize">
                    {sub?.plan_type || 'None'}
                    {c.attribution?.label && (
                      <p className="text-[11px] text-slate-500 mt-0.5" data-testid={`customer-attribution-${c.id}`}>{c.attribution.label}</p>
                    )}
                  </td>
                  <td className="py-3 px-6 text-slate-300">
                    {sites.filter(w => !w.quarantined && w.status !== 'quarantined').length}
                    {sites.some(w => w.quarantined || w.status === 'quarantined') ? (
                      <span className="text-xs text-slate-500"> · {sites.filter(w => w.quarantined || w.status === 'quarantined').length} held</span>
                    ) : null}
                  </td>
                  <td className="py-3 px-6"><StatusBadge status={c.status} /></td>
                  <td className="py-3 px-6">
                    <button onClick={() => toggleFreeHosting(c.id)} className={`px-3 py-1 rounded text-xs transition-colors ${c.free_hosting ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-500'}`} data-testid={`free-hosting-${c.id}`}>
                      {c.free_hosting ? 'Yes' : 'No'}
                    </button>
                  </td>
                  <td className="py-3 px-6 text-slate-500 text-xs">{formatDay(c.created_at)}</td>
                  <td className="py-3 px-6 text-slate-500 text-xs" data-testid={`customer-signin-${c.id}`}>{signInLabel(c)}</td>
                  <td className="py-3 px-6 text-right">
                    <button
                      type="button"
                      onClick={() => handleSendReset(c)}
                      disabled={resetBusy === c.id}
                      className="text-xs text-indigo-400 hover:text-indigo-300 disabled:opacity-50 mr-3"
                      data-testid={`send-reset-${c.id}`}
                    >
                      {resetBusy === c.id ? 'Sending…' : 'Send reset'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCustomer(c)}
                      disabled={deleteBusy === c.id}
                      className="text-xs text-slate-500 hover:text-red-400 disabled:opacity-50"
                      data-testid={`delete-customer-${c.id}`}
                    >
                      {deleteBusy === c.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
                {openId === c.id && (
                  <tr className="border-b border-slate-800/50 bg-slate-900/40">
                    <td colSpan={8} className="py-3 px-6">
                      <CustomerDeskFacts
                        client={c}
                        sub={sub}
                        payments={getClientPayments(c.id)}
                        transactions={getClientTx(c.id)}
                      />
                      {sites.length === 0 ? (
                        <p className="text-xs text-slate-500">No websites on this customer yet.</p>
                      ) : (
                        <ul className="space-y-2">
                          {sites.map((w) => (
                            <li key={w.id} className="flex flex-wrap items-center gap-2">
                              {w.site_url ? (
                                <a href={w.site_url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:underline">
                                  {w.site_name || w.site_url}
                                </a>
                              ) : (
                                <span className="text-xs text-slate-400">{w.site_name || 'Untitled site'}</span>
                              )}
                              <WebsiteReadyActions
                                website={w}
                                testIdPrefix={`customer-access-site-${w.id}`}
                                openTestId={`customer-access-site-${w.id}`}
                                editTestId={`customer-edit-site-${w.id}`}
                              />
                              {w.content_type && <span className="text-[11px] text-slate-500">{w.content_type}</span>}
                              {w.created_at && <span className="text-[11px] text-slate-500">Created {formatDay(w.created_at)}</span>}
                              {(w.quarantined || w.status === 'quarantined') && <StatusChip tone="bad">Hidden (URL kept)</StatusChip>}
                              <InviteClaimChips website={w} />
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {deleteNote && <p className="text-xs text-amber-300 mt-3" data-testid="customer-delete-note">{deleteNote}</p>}
    </div>
  );
}

// Registrars we act through. Anything else is a domain we merely track, and
// only those can be edited away again from here.
const MANAGED_REGISTRARS = ['spaceship', 'easypeazy', 'internal-qa'];

/** Days until a date, or null when there is no date to count to. */
function daysUntil(value) {
  if (!value) return null;
  const days = Math.ceil((new Date(value) - Date.now()) / 86400000);
  return Number.isFinite(days) ? days : null;
}

function adminActionError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail.map(d => d.msg).join(', ');
  return err?.message || fallback;
}

function formatWhen(value) {
  if (!value) return 'Never';
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : String(value);
}

function formatDay(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString() : '—';
}

function InviteClaimChips({ website }) {
  if (!website) return null;
  const sent = website.invite_ever_sent || (website.invite_count || 0) > 0;
  const claimed = website.invite_claimed || website.ever_accessed || website.invite_password_set;
  const next = website.invite_next_action;
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid={`invite-claim-chips-${website.id}`}>
      <StatusChip tone={sent ? 'ok' : 'slate'}>
        {sent ? `Invite sent (${website.invite_count || 0})` : 'No invite sent'}
      </StatusChip>
      {website.invite_expired && <StatusChip tone="bad">Invite expired</StatusChip>}
      <StatusChip tone={claimed ? 'ok' : 'warn'}>
        {website.ever_accessed
          ? 'Claimed — signed in'
          : website.invite_password_set
            ? 'Claimed — password set'
            : 'Still needed'}
      </StatusChip>
      {next === 'resend' && <StatusChip tone="warn" testId={`invite-next-${website.id}`}>Next: Resend</StatusChip>}
      {next === 'wait' && <StatusChip tone="info" testId={`invite-next-${website.id}`}>Next: Wait</StatusChip>}
      {next === 'help_reset' && <StatusChip tone="warn" testId={`invite-next-${website.id}`}>Next: Help reset</StatusChip>}
    </span>
  );
}

function BillingFacts({ sub, payments = [], transactions = [] }) {
  const openTx = transactions.filter(t => t.payment_status && t.payment_status !== 'paid' && t.payment_status !== 'complete');
  const hasAnything = sub || payments.length > 0 || openTx.length > 0;
  if (!hasAnything) {
    return <p className="text-xs text-slate-500" data-testid="billing-none">No Stripe / EasyPeazy billing on this customer yet.</p>;
  }
  return (
    <div className="space-y-1 text-xs text-slate-400" data-testid="billing-facts">
      {sub && (
        <p>
          Plan {sub.plan_type || 'unknown'}
          {sub.status ? ` · ${sub.status}` : ''}
          {sub.monthly_fee != null ? ` · $${sub.monthly_fee}/mo` : ''}
          {sub.next_billing_date ? ` · next ${formatDay(sub.next_billing_date)}` : ''}
        </p>
      )}
      {payments.slice(0, 5).map(p => (
        <p key={p.id}>
          {formatDay(p.payment_date || p.created_at)} · {p.payment_type || 'payment'} · ${p.amount} · {p.status || 'recorded'}
        </p>
      ))}
      {openTx.slice(0, 3).map(t => (
        <p key={t.id} className="text-amber-300">
          Checkout {t.payment_status || 'initiated'} · {t.plan_type || 'payment'} · ${t.amount}
        </p>
      ))}
    </div>
  );
}

function CustomerDeskFacts({ client, sub, payments, transactions }) {
  return (
    <div className="mb-3 space-y-1" data-testid={`customer-desk-${client.id}`}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {client.attribution?.label && <StatusChip tone={client.attribution.kind === 'initiated' ? 'info' : 'slate'}>{client.attribution.label}</StatusChip>}
        {client.ever_accessed ? <StatusChip tone="ok">Signed in {formatWhen(client.last_login)}</StatusChip> : <StatusChip tone="warn">Never signed in</StatusChip>}
        {client.free_hosting && <StatusChip tone="ok">First-year hosting on the house</StatusChip>}
      </div>
      <BillingFacts sub={sub} payments={payments} transactions={transactions} />
    </div>
  );
}

function HandoverInviteModal({ flow, clients, busy, onChangeNote, onContinue, onBack, onCancel, onSend }) {
  const { kind, step, website, email, personalNote } = flow;
  const known = (clients || []).find(c => (c.email || '').toLowerCase() === (email || '').toLowerCase());
  const invitee = handoverInviteeName(email, known);
  const preview = handoverInvitePreview({
    inviteeName: invitee,
    siteName: website.site_name,
    siteUrl: website.site_url,
    personalNote,
  });
  const isTransfer = kind === 'transfer';
  const showConfirm = isTransfer && step === 'confirm';

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="handover-invite-preview">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
              {showConfirm ? 'Confirm transfer' : isTransfer ? 'Preview invite' : 'Resend invite'}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {isTransfer ? 'Same website, new owner. The live address does not change.' : 'Emails the current owner. Does not move the website.'}
            </p>
          </div>
          <button type="button" onClick={onCancel} className="text-slate-400 hover:text-white" data-testid="handover-preview-cancel-x" aria-label="Cancel">
            <X className="w-5 h-5" />
          </button>
        </div>

        {showConfirm ? (
          <div data-testid="handover-confirm-target" className="space-y-4">
            <p className="text-sm text-slate-200">
              Transfer <span className="text-white font-medium">{website.site_name || 'this website'}</span>
              {website.site_url ? <> at <span className="text-indigo-300 break-all">{website.site_url}</span></> : null}
              {' '}to <span className="text-white font-medium break-all">{email}</span>?
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg text-xs text-slate-300 bg-slate-800 hover:bg-slate-700" data-testid="handover-confirm-cancel">Cancel</button>
              <button type="button" onClick={onContinue} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-indigo-500 hover:bg-indigo-600" data-testid="handover-confirm-continue">Continue to invite preview</button>
            </div>
          </div>
        ) : (
          <div data-testid="handover-preview-body" className="space-y-3">
            <p className="text-xs text-slate-400">
              {isTransfer ? `Then send the handover invite to ${email}.` : `Resend the handover invite to current owner ${email}.`}
            </p>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Subject</p>
              <p className="text-sm text-slate-200" data-testid="handover-preview-subject">{preview.subject}</p>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 space-y-2 text-sm text-slate-300" data-testid="handover-preview-default-copy">
              <p>{preview.greeting}</p>
              <p>{preview.liveLine}</p>
              {preview.personalNote && <p className="text-slate-100 whitespace-pre-wrap" data-testid="handover-preview-note">{preview.personalNote}</p>}
              <p>{preview.setPassword}</p>
              <p className="font-medium text-white">{preview.cta}</p>
              <p className="text-xs text-slate-500">{preview.linkNote}</p>
            </div>
            <div>
              <label htmlFor="handover-personal-note" className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Optional personal comments</label>
              <textarea
                id="handover-personal-note"
                value={personalNote}
                onChange={e => onChangeNote(e.target.value.slice(0, PERSONAL_NOTE_MAX))}
                rows={3}
                maxLength={PERSONAL_NOTE_MAX}
                placeholder="e.g. This is a gift. I am not expecting anything. This is not spam."
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 outline-none focus:border-indigo-500"
                data-testid="handover-personal-note"
              />
              <p className="text-[11px] text-slate-500 mt-1">Added as a short paragraph in the invite. Leave blank to send the default copy only.</p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              {isTransfer && <button type="button" onClick={onBack} className="px-3 py-1.5 rounded-lg text-xs text-slate-300 bg-slate-800 hover:bg-slate-700" data-testid="handover-preview-back">Back</button>}
              <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded-lg text-xs text-slate-300 bg-slate-800 hover:bg-slate-700" data-testid="handover-preview-cancel">Cancel</button>
              <button type="button" onClick={onSend} disabled={busy} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50" data-testid="handover-preview-send">
                {busy ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function ExpiryNote({ value }) {
  const days = daysUntil(value);
  if (days === null) return <span className="text-xs text-slate-600">No renewal date</span>;
  const when = new Date(value).toLocaleDateString();
  // Expired and nearly-expired are the only two states worth colouring. Making
  // every row shout leaves nothing to notice.
  const tone = days < 0 ? 'text-red-400' : days <= 30 ? 'text-amber-400' : 'text-slate-500';
  const label = days < 0 ? `Expired ${when}` : days <= 60 ? `${days}d — ${when}` : when;
  return <span className={`text-xs ${tone}`}>{label}</span>;
}

function AdminWebsites({ startBringOver = false } = {}) {
  const [data, setData] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [domainForm, setDomainForm] = useState({ domain_name: '', registrar: 'enom', expiration_date: '', renew_cost: '' });
  const [domainError, setDomainError] = useState('');
  const [saving, setSaving] = useState(false);
  const [transferEmail, setTransferEmail] = useState({});
  const [transferBusy, setTransferBusy] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(null);
  const [handoverFlow, setHandoverFlow] = useState(null);
  const [actionNote, setActionNote] = useState({});
  const [showQuarantined, setShowQuarantined] = useState(false);
  const [deleteMode, setDeleteMode] = useState('quarantine');
  const [deleteBusy, setDeleteBusy] = useState(null);
  const [policyBusy, setPolicyBusy] = useState(null);
  const [reserveForm, setReserveForm] = useState({ label: '', base: EASY_PEAZY_SUBDOMAIN_BASE, note: '' });
  const [reserveBusy, setReserveBusy] = useState(false);
  const [reserveError, setReserveError] = useState('');
  const [attachForm, setAttachForm] = useState({ domain_name: '', registrar: '' });
  const [attachBusy, setAttachBusy] = useState(false);
  const [attachResult, setAttachResult] = useState(null);
  const [transferQuote, setTransferQuote] = useState(null);
  const [transferQuoteError, setTransferQuoteError] = useState('');
  const [transferAuth, setTransferAuth] = useState('');
  const [transferConfirm, setTransferConfirm] = useState(false);
  const [transferInBusy, setTransferInBusy] = useState(false);
  const [transferInResult, setTransferInResult] = useState(null);
  const [sortKey, setSortKey] = useState('created_at_desc');
  const [filterKey, setFilterKey] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showBringOver, setShowBringOver] = useState(!!startBringOver);

  useEffect(() => {
    if (startBringOver) setShowBringOver(true);
  }, [startBringOver]);

  const reload = useCallback(() => {
    api.getAdminWebsites().then(r => setData(r.data)).catch(console.error);
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { setDeleteMode('quarantine'); }, [detailId]);
  useEffect(() => {
    setAttachResult(null);
    setAttachForm({ domain_name: '', registrar: '' });
    setTransferQuote(null);
    setTransferQuoteError('');
    setTransferAuth('');
    setTransferConfirm(false);
    setTransferInResult(null);
  }, [detailId]);

  useEffect(() => {
    const name = (attachForm.domain_name || '').trim();
    if (!name || !name.includes('.')) {
      setTransferQuote(null);
      setTransferQuoteError('');
      return undefined;
    }
    let cancelled = false;
    api.quoteDomainTransfer(name).then((r) => {
      if (!cancelled) {
        setTransferQuote(r.data);
        setTransferQuoteError('');
      }
    }).catch((err) => {
      if (!cancelled) {
        setTransferQuote(null);
        setTransferQuoteError(err?.response?.data?.detail || '');
      }
    });
    return () => { cancelled = true; };
  }, [attachForm.domain_name]);

  const addDomain = async (e) => {
    e.preventDefault();
    setDomainError('');
    setSaving(true);
    try {
      await api.addAdminDomain({
        domain_name: domainForm.domain_name,
        registrar: domainForm.registrar,
        expiration_date: domainForm.expiration_date || null,
        renew_cost: domainForm.renew_cost ? Number(domainForm.renew_cost) : null,
      });
      setDomainForm({ domain_name: '', registrar: 'enom', expiration_date: '', renew_cost: '' });
      setAdding(false);
      reload();
    } catch (err) {
      setDomainError(err?.response?.data?.detail || 'Could not add that domain.');
    }
    setSaving(false);
  };

  const attachOwnedDomain = async (e, website) => {
    e.preventDefault();
    if (!website?.id || !attachForm.domain_name.trim()) return;
    setAttachBusy(true);
    setAttachResult(null);
    try {
      const r = await api.attachAdminDomain(website.id, {
        domain_name: attachForm.domain_name,
        registrar: attachForm.registrar || 'external',
      });
      setAttachResult(r.data);
      reload();
    } catch (err) {
      setAttachResult({ error: err?.response?.data?.detail || 'Could not attach that domain.' });
    }
    setAttachBusy(false);
  };

  const startDomainTransferIn = async (e, website) => {
    e.preventDefault();
    if (!website?.id || !attachForm.domain_name.trim() || !transferQuote || !transferConfirm) return;
    setTransferInBusy(true);
    setTransferInResult(null);
    try {
      const r = await api.transferAdminDomainIn(website.id, {
        domain_name: attachForm.domain_name,
        auth_code: transferAuth,
        confirm_charge: true,
        quoted_amount: transferQuote.amount,
      });
      setTransferInResult(r.data);
      reload();
    } catch (err) {
      setTransferInResult({ error: err?.response?.data?.detail || 'Could not start that transfer.' });
    }
    setTransferInBusy(false);
  };

  const removeDomain = async (d) => {
    if (!window.confirm(`Stop tracking ${d.domain_name}? This only removes it from this list — the domain itself is untouched.`)) return;
    try {
      await api.deleteAdminDomain(d.id);
      reload();
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'Could not remove that entry.');
    }
  };

  if (!data) return <div className="text-slate-500">Loading...</div>;

  const getClient = (cid) => data.clients?.find(c => c.id === cid);
  const getWebsite = (wid) => data.websites?.find(w => w.id === wid);

  // Soonest renewal first — that ordering is the whole point of the list, since
  // it says which domain is worth acting on next. Undated ones sort to the end.
  const domains = [...(data.domains || [])]
    .filter(d => d.status !== 'reserved' && (d.registrar || '').toLowerCase() !== 'reserved')
    .sort((a, b) => {
    if (!a.expiration_date && !b.expiration_date) return a.domain_name.localeCompare(b.domain_name);
    if (!a.expiration_date) return 1;
    if (!b.expiration_date) return -1;
    return new Date(a.expiration_date) - new Date(b.expiration_date);
  });

  const handleReprovision = async (website) => {
    if (!window.confirm(`Re-run provisioning for ${website.site_name}?`)) return;
    try {
      await api.reprovisionWebsite(website.id);
      api.getAdminWebsites().then(r => setData(r.data));
    } catch (err) { console.error(err); }
  };

  const startTransfer = (website) => {
    const email = (transferEmail[website.id] || '').trim();
    if (!email) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: 'Type the new owner email, then Transfer.' } }));
      return;
    }
    setActionNote(n => ({ ...n, [website.id]: '' }));
    // Close the site drawer first. Resend lives only there; the Sheet portal
    // (z-50 + focus trap) hid the preview that Transfer on the card could show.
    setDetailId(null);
    setHandoverFlow({ kind: 'transfer', step: 'confirm', website, email, personalNote: '' });
  };

  const startResendInvite = (website) => {
    const current = (getClient(website.client_id)?.email || website.owner_email || '').trim().toLowerCase();
    if (!current) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: 'This website has no owner email to invite.' } }));
      return;
    }
    // Transfer Cancel leaves the typed new-owner email in the field. That
    // field is for Transfer. Resend always previews the current owner — the
    // live FAIL skipped the preview when those addresses differed.
    setActionNote(n => ({ ...n, [website.id]: '' }));
    setDetailId(null);
    setHandoverFlow({ kind: 'resend', step: 'preview', website, email: current, personalNote: '' });
  };

  const handleTransfer = async (website, { email, personal_note } = {}) => {
    const dest = (email || transferEmail[website.id] || '').trim();
    if (!dest) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: 'Type the new owner email, then Transfer.' } }));
      return;
    }
    setTransferBusy(website.id);
    setActionNote(n => ({ ...n, [website.id]: '' }));
    try {
      const res = await api.transferWebsiteOwner(website.id, { owner_email: dest, send_invite: true, personal_note: personal_note || '' });
      const ownerEmail = res.data.owner?.email || dest;
      const invited = res.data.owner?.invited;
      const count = res.data.invite_count;
      const extra = (res.data.notes || []).join(' ');
      const siteName = res.data.site_name || website.site_name || '';
      const siteUrl = res.data.site_url || website.site_url || '';
      const invitee = res.data.owner?.invitee_name || '';
      const text = invited
        ? `Now owned by ${ownerEmail}. Invitation sent to ${invitee ? `${invitee} ` : ''}${res.data.owner?.invite_email || ownerEmail} for ${siteName} at ${siteUrl}${typeof count === 'number' ? ` (${count} sent)` : ''}.`
        : `Now owned by ${ownerEmail}. Invitation was not sent.${extra ? ` ${extra}` : ''}`;
      setTransferEmail(e => ({ ...e, [website.id]: '' }));
      setActionNote(n => ({ ...n, [website.id]: { kind: 'ok', text } }));
      setData(d => {
        if (!d) return d;
        const ownerId = res.data.owner?.client_id;
        return {
          ...d,
          websites: (d.websites || []).map(row => row.id === website.id ? {
            ...row,
            client_id: ownerId || row.client_id,
            owner_email: ownerEmail,
            invite_count: typeof count === 'number' ? count : row.invite_count,
            invite_ever_sent: true,
            invite_still_needed: true,
          } : row),
        };
      });
      reload();
    } catch (err) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: adminActionError(err, 'Could not transfer that website.') } }));
    }
    setTransferBusy(null);
  };

  const handleResendInvite = async (website, { personal_note } = {}) => {
    setInviteBusy(website.id);
    setActionNote(n => ({ ...n, [website.id]: '' }));
    try {
      const res = await api.resendWebsiteInvite(website.id, { personal_note: personal_note || '' });
      const siteName = res.data.site_name || website.site_name || '';
      const siteUrl = res.data.site_url || website.site_url || '';
      const invitee = res.data.invitee_name || '';
      setActionNote(n => ({ ...n, [website.id]: { kind: 'ok', text: `Invitation sent to current owner ${invitee ? `${invitee} ` : ''}${res.data.email} for ${siteName} at ${siteUrl} (${res.data.invite_count} sent). The website was not moved.` } }));
      reload();
    } catch (err) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: adminActionError(err, 'Could not send the invitation.') } }));
    }
    setInviteBusy(null);
  };

  const sendHandoverPreview = async () => {
    if (!handoverFlow) return;
    const { website, email, personalNote, kind } = handoverFlow;
    if (kind === 'transfer') {
      await handleTransfer(website, { email, personal_note: personalNote });
    } else {
      await handleResendInvite(website, { personal_note: personalNote });
    }
    setHandoverFlow(null);
  };

  const handleDeleteWebsite = async (website, mode) => {
    const free = (mode || deleteMode) === 'free';
    const ok = window.confirm(free
      ? `Delete & free URL for ${website.site_name || website.site_url}? This removes the website, tears down the published site, and deletes the DNS name so someone else can claim that address. Use this only on a test or fake site.`
      : `Hide & keep URL for ${website.site_name || website.site_url}? The site leaves this list, but the address stays held so nobody else can claim it.`);
    if (!ok) return;
    setDeleteBusy(website.id);
    setActionNote(n => ({ ...n, [website.id]: '' }));
    try {
      const res = await api.deleteAdminWebsite(website.id, { mode: free ? 'free' : 'quarantine' });
      setActionNote(n => ({ ...n, [website.id]: { kind: 'ok', text: free ? 'URL freed. Website and DNS name removed.' : 'Hidden. Off the list; the URL is still held.' } }));
      if (free && detailId === website.id) setDetailId(null);
      if ((res.data.notes || []).length) {
        setActionNote(n => ({ ...n, [website.id]: { kind: 'ok', text: `Freed. ${(res.data.notes || []).join(' ')}` } }));
      }
      reload();
    } catch (err) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: adminActionError(err, 'Could not delete that website.') } }));
    }
    setDeleteBusy(null);
  };

  const handleContentPolicy = async (website, flag) => {
    if (!window.confirm(`Flag ${website.site_name || 'this site'} as ${flag} and hide it? The URL stays held so nobody else can claim it.`)) return;
    setPolicyBusy(website.id);
    try {
      await api.flagWebsiteContent(website.id, { flag });
      setActionNote(n => ({ ...n, [website.id]: { kind: 'ok', text: `Flagged ${flag} and hidden. The URL is still held.` } }));
      reload();
    } catch (err) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: adminActionError(err, 'Could not flag that website.') } }));
    }
    setPolicyBusy(null);
  };

  const handleSendReset = async (website) => {
    const client = getClient(website.client_id);
    if (!client?.id) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: 'No customer login to reset.' } }));
      return;
    }
    if (!window.confirm(`Send a password reset to ${client.email}? They get a 1-hour link. This is help for a locked-out customer, not a new invite.`)) return;
    setInviteBusy(website.id);
    try {
      const res = await api.sendAdminPasswordReset(client.id);
      setActionNote(n => ({ ...n, [website.id]: { kind: 'ok', text: res.data.message || `Password reset sent to ${client.email}.` } }));
    } catch (err) {
      setActionNote(n => ({ ...n, [website.id]: { kind: 'error', text: adminActionError(err, 'Could not send a password reset.') } }));
    }
    setInviteBusy(null);
  };

  const handleReserve = async (e) => {
    e.preventDefault();
    setReserveError('');
    setReserveBusy(true);
    try {
      await api.reserveAdminLabel(reserveForm);
      setReserveForm({ label: '', base: EASY_PEAZY_SUBDOMAIN_BASE, note: '' });
      reload();
    } catch (err) {
      setReserveError(adminActionError(err, 'Could not reserve that label.'));
    }
    setReserveBusy(false);
  };

  const handleRelease = async (domain) => {
    if (!window.confirm(`Release URL ${domain.domain_name}? Someone else can claim that address after this.`)) return;
    try {
      await api.releaseAdminLabel(domain.id);
      reload();
    } catch (err) {
      window.alert(adminActionError(err, 'Could not release that hold.'));
    }
  };

  const detail = detailId ? data.websites?.find(w => w.id === detailId) : null;
  const detailClient = detail ? getClient(detail.client_id) : null;
  const getPlan = (cid) => (data.subscriptions || []).find(s => s.client_id === cid)?.plan_type;
  const reservedLabels = data.reserved_labels || (data.domains || []).filter(d => d.status === 'reserved' || d.registrar === 'reserved');
  const allWebsites = data.websites || [];
  const liveCount = allWebsites.filter(isLiveWebsite).length;
  const hiddenCount = allWebsites.filter(isHiddenWebsite).length;
  const errorCount = allWebsites.filter(w => isErrorWebsite(w) && !isHiddenWebsite(w)).length;
  const toggleWebsiteFilter = (key) => setFilterKey(current => current === key ? 'all' : key);
  const listedWebsites = allWebsites
    .filter((w) => {
      if (filterKey === 'live') return isLiveWebsite(w);
      if (filterKey === 'hidden') return isHiddenWebsite(w);
      if (filterKey === 'error') return isErrorWebsite(w) && !isHiddenWebsite(w);
      return showQuarantined || !isHiddenWebsite(w);
    })
    .slice()
    .sort((a, b) => {
      const aOwner = getClient(a.client_id) || a;
      const bOwner = getClient(b.client_id) || b;
      if (sortKey === 'created_at_asc') return (timeMs(a.created_at) || 0) - (timeMs(b.created_at) || 0);
      if (sortKey === 'last_login_desc') return compareTimeDesc(aOwner.last_login || a.last_login, bOwner.last_login || b.last_login);
      if (sortKey === 'last_login_never') return compareTimeNeverFirst(aOwner.last_login || a.last_login, bOwner.last_login || b.last_login);
      if (sortKey === 'sister_count_desc') return (b.sister_count || 0) - (a.sister_count || 0);
      if (sortKey === 'content_type') return String(a.content_type || a.status || '').localeCompare(String(b.content_type || b.status || ''));
      if (sortKey === 'status') return String(a.status || '').localeCompare(String(b.status || ''));
      return (timeMs(b.created_at) || 0) - (timeMs(a.created_at) || 0);
    });

  return (
    <div className="animate-fade-in" data-testid="admin-websites">
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Websites & Domains</h2>
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => { setShowBringOver((open) => !open); setShowCreate(false); }}
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg text-sm border border-slate-600 transition-all"
            data-testid="bring-site-over-btn"
          >
            <ArrowRightLeft className="w-4 h-4" /> Bring a site over
          </button>
          <button
            type="button"
            onClick={() => { setShowCreate((open) => !open); setShowBringOver(false); }}
            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm transition-all"
            data-testid="create-new-website-btn"
          >
            <Plus className="w-4 h-4" /> Create new website
          </button>
        </div>
      </div>
      {showBringOver ? (
        <div className="mb-6 rounded-xl border border-slate-800 bg-slate-900/40 p-5" data-testid="bring-site-over-panel">
          <AdminSiteImport
            embedded
            onBuilt={(payload) => {
              reload();
              if (payload?.website?.id) setDetailId(payload.website.id);
            }}
          />
        </div>
      ) : null}
      {showCreate ? (
        <div className="mb-6 max-w-md" data-testid="create-new-website-panel">
          <AdminCreateSiteCard
            onCreated={(payload) => {
              reload();
              if (payload?.website?.id) setDetailId(payload.website.id);
            }}
          />
        </div>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-6" data-testid="websites-summary-cards">
        <DeskCard label="Live" value={liveCount} hint="Not hidden or error" active={filterKey === 'live'} onClick={() => toggleWebsiteFilter('live')} testId="websites-card-live" icon={Server} color="text-cyan-400" bg="bg-cyan-500/10" />
        <DeskCard label="Hidden (URL kept)" value={hiddenCount} hint="Still held" active={filterKey === 'hidden'} onClick={() => { setShowQuarantined(true); toggleWebsiteFilter('hidden'); }} testId="websites-card-hidden" icon={EyeOff} color="text-rose-300" bg="bg-rose-500/10" />
        <DeskCard label="Error" value={errorCount} hint="Build failed" active={filterKey === 'error'} onClick={() => toggleWebsiteFilter('error')} testId="websites-card-error" icon={AlertCircle} color="text-amber-400" bg="bg-amber-500/10" />
        <DeskCard label="Reserved URLs" value={reservedLabels.length} hint="Admin holds" active={false} onClick={() => scrollToDesk('reserved-labels')} testId="websites-card-reserved" icon={Tag} color="text-indigo-400" bg="bg-indigo-500/10" />
        <DeskCard label="Domains" value={domains.length} hint="Tracked renewals" active={false} onClick={() => scrollToDesk('tracked-domains')} testId="websites-card-domains" icon={Globe} color="text-purple-400" bg="bg-purple-500/10" />
      </div>

      <Sheet open={!!detail} onOpenChange={(open) => { if (!open) setDetailId(null); }}>
        <SheetContent side="right" className="bg-slate-950 border-slate-800 text-slate-100 sm:max-w-md overflow-y-auto" data-testid="website-detail-drawer">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="text-white">{detail.site_name || 'Website'}</SheetTitle>
                <SheetDescription className="text-slate-400">
                  Chips first. Hide keeps the URL. Delete & free URL tears the name down. Transfer moves ownership.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="flex flex-wrap gap-1.5" data-testid={`drawer-status-chips-${detail.id}`}>
                  <StatusChip tone={detail.status === 'active' ? 'ok' : (detail.quarantined || detail.status === 'quarantined') ? 'bad' : 'warn'}>{statusLabel(detail.status) || 'unknown'}</StatusChip>
                  <InviteClaimChips website={detail} />
                  <StatusChip tone="info">{detailClient?.email || detail.owner_email || 'No owner'}</StatusChip>
                  <StatusChip>{getPlan(detail.client_id) || 'No plan'}</StatusChip>
                  {detail.content_type && <StatusChip>{detail.content_type}</StatusChip>}
                  {(detail.attribution || detailClient?.attribution)?.label && (
                    <StatusChip tone={(detail.attribution || detailClient?.attribution).kind === 'initiated' ? 'info' : 'slate'}>
                      {(detail.attribution || detailClient?.attribution).label}
                    </StatusChip>
                  )}
                  {detail.content_policy && <StatusChip tone="bad">{detail.content_policy}</StatusChip>}
                </div>
                <div data-testid={`drawer-full-facts-${detail.id}`}>
                  <p className="text-slate-200 break-all">{detail.site_url || 'No live address yet'}</p>
                  <p className="text-xs text-slate-500 mt-1">Created {formatDay(detail.created_at)}</p>
                  <p className="text-xs text-slate-400 mt-1">{detailClient?.full_name || detail.owner_name || 'Unnamed'} · {detailClient?.email || detail.owner_email || 'No email'}</p>
                  {detailClient?.phone && <p className="text-slate-500 text-xs">{detailClient.phone}</p>}
                  <p className="text-xs text-slate-500">
                    {detailClient?.free_hosting ? 'First-year hosting on the house' : 'No free hosting'}
                    {detailClient?.created_at ? ` · joined ${formatDay(detailClient.created_at)}` : ''}
                  </p>
                  {(detail.sister_sites || []).length > 0 && (
                    <p className="text-xs text-slate-400 mt-1" data-testid={`drawer-sisters-${detail.id}`}>
                      Also this customer: {(detail.sister_sites || []).map(s => s.site_name || s.site_url).join(', ')}
                    </p>
                  )}
                  <div className="mt-3 space-y-1 text-xs text-slate-400" data-testid={`invite-ops-${detail.id}`}>
                    <p>Invite sent: {detail.invite_ever_sent ? 'Yes' : 'No'} · first {formatWhen(detail.first_invite_at)} · last {formatWhen(detail.last_invite_at)} · count {typeof detail.invite_count === 'number' ? detail.invite_count : 0}</p>
                    <p>Invite expired: {detail.invite_expired ? `Yes — ${formatWhen(detail.invite_expires_at)}` : (detail.invite_expires_at ? `No — open until ${formatWhen(detail.invite_expires_at)}` : 'No invite to expire')}</p>
                    <p>Claimed: {detail.ever_accessed ? `signed in ${formatWhen(detail.last_login)}` : detail.invite_password_set ? 'password set, not signed in yet' : 'still needed'}</p>
                    <p>Next action: {detail.invite_next_action_label || '—'}</p>
                    {detail.edit_url && (
                      <p className="text-slate-500">
                        Editor:{' '}
                        <button
                          type="button"
                          onClick={() => openEditor(detail, detail.edit_url)}
                          className="text-indigo-400 hover:text-indigo-300 break-all text-left"
                          data-testid={`drawer-edit-site-${detail.id}`}
                        >
                          {detail.edit_url}
                        </button>
                      </p>
                    )}
                  </div>
                  <div className="mt-3">
                    <BillingFacts
                      sub={(data.subscriptions || []).find(s => s.client_id === detail.client_id)}
                      payments={(data.payments || []).filter(p => p.client_id === detail.client_id)}
                      transactions={(data.payment_transactions || []).filter(p => p.client_id === detail.client_id)}
                    />
                  </div>
                </div>
                <WebsiteReadyActions website={detail} testIdPrefix={`drawer-access-site-${detail.id}`} />
                <p className="text-[11px] text-slate-500">
                  Open website shows the live page. Edit this website is the owner area — you stay signed in. There is no extra login on the public site.
                </p>
                <form onSubmit={(e) => attachOwnedDomain(e, detail)} className="border-t border-slate-800 pt-3 space-y-2" data-testid={`drawer-attach-domain-${detail.id}`}>
                  <p className="text-xs font-medium text-slate-300">Use the address they already own</p>
                  <p className="text-[11px] text-slate-500">
                    This does not buy a domain. They keep it wherever it is registered today. After we attach it, they change the nameservers there to the two names we show. Until that address answers, keep using {detail.site_url || 'the EasyPeazy address'}.
                  </p>
                  <input
                    value={attachForm.domain_name}
                    onChange={e => setAttachForm(f => ({ ...f, domain_name: e.target.value }))}
                    placeholder="Enter your current web address (e.g. yourbusiness.com)"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-2 text-xs text-slate-100 placeholder:text-slate-500 outline-none"
                    data-testid={`drawer-attach-domain-name-${detail.id}`}
                  />
                  <select
                    value={attachForm.registrar}
                    onChange={e => setAttachForm(f => ({ ...f, registrar: e.target.value }))}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-2 text-xs text-white"
                    data-testid={`drawer-attach-registrar-${detail.id}`}
                  >
                    <option value="" disabled>Where is it registered?</option>
                    <option value="godaddy">GoDaddy</option>
                    <option value="namecheap">Namecheap</option>
                    <option value="enom">Enom</option>
                    <option value="dreamhost">DreamHost</option>
                    <option value="external">Somewhere else / not sure</option>
                  </select>
                  <button type="submit" disabled={attachBusy || !attachForm.domain_name.trim()} className="inline-flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`drawer-attach-domain-submit-${detail.id}`}>
                    <Globe className="w-3.5 h-3.5" /> {attachBusy ? 'Attaching…' : 'Use this domain'}
                  </button>
                  {attachResult && (
                    <div className="rounded-lg bg-slate-900/70 border border-slate-800 p-3 space-y-2" data-testid={`drawer-attach-domain-result-${detail.id}`}>
                      {attachResult.error && <p className="text-xs text-red-400">{attachResult.error}</p>}
                      {attachResult.message && <p className="text-xs text-slate-300">{attachResult.message}</p>}
                      {(attachResult.nameservers || []).length > 0 && (
                        <div>
                          <p className="text-[11px] text-slate-500 mb-1">Nameservers to paste where the address is registered</p>
                          {(attachResult.nameservers || []).map((ns) => (
                            <p key={ns} className="text-xs font-mono text-indigo-300">{ns}</p>
                          ))}
                        </div>
                      )}
                      {attachResult.dns?.mail_warning && (
                        <p className="text-[11px] text-amber-300">{attachResult.dns.mail_warning}</p>
                      )}
                      {(attachResult.dns?.steps || []).length > 0 && (
                        <ol className="list-decimal list-inside space-y-1 text-[11px] text-slate-400">
                          {attachResult.dns.steps.map((step, i) => <li key={i}>{step}</li>)}
                        </ol>
                      )}
                    </div>
                  )}
                </form>
                <form onSubmit={(e) => startDomainTransferIn(e, detail)} className="space-y-2" data-testid={`drawer-transfer-in-${detail.id}`}>
                  <p className="text-xs font-medium text-slate-300">Transfer your web address (domain) to us so we manage it — optional</p>
                  <p className="text-[11px] text-slate-500">
                    What moves: the registration of the name itself (the yearly renewal and its settings) comes to EasyPeazy, and we point it at the website and keep it renewed. The website, email forwarding and the address people type stay the same. You need the transfer code (auth/EPP code) from where it is registered now. The price — usually about one year of renewal — is shown before any charge. Keeping it where it is (above) still works.
                  </p>
                  {transferQuote && (
                    <div className="rounded-lg bg-slate-900/70 border border-slate-800 p-3 space-y-1" data-testid={`drawer-transfer-in-price-${detail.id}`}>
                      <p className="text-xs text-slate-200">
                        {transferQuote.easier ? 'Easier path when you want us to handle DNS: ' : 'Transfer price: '}
                        ${Number(transferQuote.amount).toFixed(2)}
                        {transferQuote.includes_renewal_year ? ' — includes about a year of renewal' : ''}.
                      </p>
                      <p className="text-[11px] text-slate-500">No charge until you confirm this amount. NS-attach still works without transferring.</p>
                    </div>
                  )}
                  {transferQuoteError && (
                    <p className="text-xs text-amber-300" data-testid={`drawer-transfer-in-unpriced-${detail.id}`}>
                      {transferQuoteError} NS-attach above still works.
                    </p>
                  )}
                  <input
                    value={transferAuth}
                    onChange={e => setTransferAuth(e.target.value)}
                    placeholder="Auth / EPP code"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-2 text-xs text-slate-100 placeholder:text-slate-500 outline-none"
                    data-testid={`drawer-transfer-in-auth-${detail.id}`}
                  />
                  <label className="flex items-start gap-2 text-[11px] text-slate-400">
                    <input
                      type="checkbox"
                      checked={transferConfirm}
                      onChange={e => setTransferConfirm(e.target.checked)}
                      className="mt-0.5"
                      data-testid={`drawer-transfer-in-confirm-${detail.id}`}
                    />
                    <span>
                      I confirm the ${transferQuote ? Number(transferQuote.amount).toFixed(2) : 'quoted'} transfer price before any charge.
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={transferInBusy || !attachForm.domain_name.trim() || !transferQuote || !transferConfirm || !transferAuth.trim()}
                    className="inline-flex items-center gap-1.5 bg-slate-700/50 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                    data-testid={`drawer-transfer-in-submit-${detail.id}`}
                  >
                    {transferInBusy ? 'Quoting…' : 'Transfer my web address to EasyPeazy'}
                  </button>
                  {transferInResult && (
                    <div className="rounded-lg bg-slate-900/70 border border-slate-800 p-3 space-y-1" data-testid={`drawer-transfer-in-result-${detail.id}`}>
                      {transferInResult.error && <p className="text-xs text-red-400">{transferInResult.error}</p>}
                      {transferInResult.message && <p className="text-xs text-slate-300">{transferInResult.message}</p>}
                      {transferInResult.held && <p className="text-[11px] text-amber-300">On hold: no transfer started and no charge.</p>}
                    </div>
                  )}
                </form>
                <form onSubmit={(e) => { e.preventDefault(); startTransfer(detail); }} className="space-y-2" data-testid={`drawer-transfer-form-${detail.id}`}>
                  <p className="text-xs text-slate-400">Transfer confirms this website and email, then shows the invite you will send.</p>
                  <input
                    type="email"
                    value={transferEmail[detail.id] || ''}
                    onChange={e => setTransferEmail(m => ({ ...m, [detail.id]: e.target.value }))}
                    placeholder="New owner email"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-2 text-xs text-slate-100 placeholder:text-slate-500 outline-none"
                    data-testid={`drawer-transfer-email-${detail.id}`}
                  />
                  <button type="submit" disabled={transferBusy === detail.id} className="inline-flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`drawer-transfer-owner-${detail.id}`}>
                    <ArrowRightLeft className="w-3.5 h-3.5" /> {transferBusy === detail.id ? 'Transferring…' : 'Transfer to this email'}
                  </button>
                </form>
                <div className="border-t border-slate-800 pt-3" data-testid={`drawer-people-${detail.id}`}>
                  <p className="text-[11px] text-slate-500 mb-1">One owner pays and owns it. Add as many editors as you like — each gets an invitation and edits as themselves.</p>
                  <WebsitePeople website={detail} canManage />
                </div>
                <div className="border-t border-slate-800 pt-3 space-y-2">
                  <p className="text-[11px] text-slate-500 mb-2">Opens the same invite preview as Transfer, then emails the current owner a sign-in link. Does not move the website. Resend is a new 7-day invite. Send reset is a 1-hour password link for someone who is locked out.</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => startResendInvite(detail)} disabled={inviteBusy === detail.id} className="inline-flex items-center gap-1.5 bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`resend-invite-${detail.id}`}>
                      <Mail className="w-3.5 h-3.5" /> {inviteBusy === detail.id ? 'Sending…' : 'Resend invite (current owner)'}
                    </button>
                    <button type="button" onClick={() => handleSendReset(detail)} disabled={inviteBusy === detail.id} className="inline-flex items-center gap-1.5 bg-slate-700/50 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`drawer-send-reset-${detail.id}`}>
                      <Mail className="w-3.5 h-3.5" /> {inviteBusy === detail.id ? 'Sending…' : 'Send password reset'}
                    </button>
                  </div>
                </div>
                <div className="border-t border-slate-800 pt-3 space-y-2" data-testid={`drawer-delete-${detail.id}`}>
                  <p className="text-[11px] text-slate-500">Both are delete. Hide keeps the address so Bring Over cannot steal it. Delete & free URL removes the published site and the DNS name — test/fake sites only.</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => { setDeleteMode('quarantine'); handleDeleteWebsite(detail, 'quarantine'); }} disabled={deleteBusy === detail.id} className="inline-flex items-center gap-1.5 bg-slate-700/50 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`delete-mode-quarantine-${detail.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> {deleteBusy === detail.id && deleteMode === 'quarantine' ? 'Hiding…' : 'Hide & keep URL'}
                    </button>
                    <button type="button" onClick={() => { setDeleteMode('free'); handleDeleteWebsite(detail, 'free'); }} disabled={deleteBusy === detail.id} className="inline-flex items-center gap-1.5 bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" data-testid={`delete-mode-free-${detail.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> {deleteBusy === detail.id && deleteMode === 'free' ? 'Freeing…' : 'Delete & free URL'}
                    </button>
                  </div>
                  <button type="button" onClick={() => handleDeleteWebsite(detail, deleteMode)} disabled={deleteBusy === detail.id} className="sr-only" data-testid={`delete-website-${detail.id}`} tabIndex={-1} aria-hidden="true">
                    {deleteMode === 'free' ? 'Delete & free URL' : 'Hide & keep URL'}
                  </button>
                </div>
                <div className="border-t border-slate-800 pt-3 space-y-2" data-testid={`drawer-content-policy-${detail.id}`}>
                  <p className="text-[11px] text-slate-500">Content policy — flag and hide. The URL stays held. Not a Trust & Safety product.</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => handleContentPolicy(detail, 'adult')} disabled={policyBusy === detail.id} className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-lg disabled:opacity-50" data-testid={`flag-adult-${detail.id}`}>Adult</button>
                    <button type="button" onClick={() => handleContentPolicy(detail, 'scam')} disabled={policyBusy === detail.id} className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-lg disabled:opacity-50" data-testid={`flag-scam-${detail.id}`}>Unethical scam</button>
                  </div>
                </div>
                {actionNote[detail.id] && (
                  <p className={`text-xs ${actionNote[detail.id].kind === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{actionNote[detail.id].text || actionNote[detail.id]}</p>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {handoverFlow && (
        <HandoverInviteModal
          flow={handoverFlow}
          clients={data.clients || []}
          busy={transferBusy === handoverFlow.website.id || inviteBusy === handoverFlow.website.id}
          onChangeNote={(personalNote) => setHandoverFlow(f => f ? { ...f, personalNote } : f)}
          onContinue={() => setHandoverFlow(f => f ? { ...f, step: 'preview' } : f)}
          onBack={() => setHandoverFlow(f => f ? { ...f, step: 'confirm' } : f)}
          onCancel={() => setHandoverFlow(null)}
          onSend={sendHandoverPreview}
        />
      )}

      {/* All Websites */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 mb-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>All Websites ({listedWebsites.length})</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <DeskSort
              value={sortKey}
              onChange={setSortKey}
              testId="websites-sort"
              options={[
                { value: 'created_at_desc', label: 'Created — newest' },
                { value: 'created_at_asc', label: 'Created — oldest' },
                { value: 'last_login_desc', label: 'EasyPeazy sign-in — recent' },
                { value: 'last_login_never', label: 'EasyPeazy sign-in — never first' },
                { value: 'sister_count_desc', label: 'Sister sites' },
                { value: 'content_type', label: 'Content type' },
                { value: 'status', label: 'Status' },
              ]}
            />
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input type="checkbox" checked={showQuarantined || filterKey === 'hidden'} onChange={e => { setShowQuarantined(e.target.checked); if (!e.target.checked && filterKey === 'hidden') setFilterKey('all'); }} data-testid="show-quarantined" />
              Show hidden (URL kept)
            </label>
          </div>
        </div>
        {listedWebsites.length > 0 ? (
          <div className="space-y-2">
            {listedWebsites.map(w => {
              const client = getClient(w.client_id);
              const ownerEmail = client?.email || w.owner_email || client?.full_name || 'Unknown';
              const ownerSignIn = client?.last_login || w.last_login;
              return (
                <div key={w.id} className="rounded-lg bg-slate-800/30 border border-slate-800 overflow-hidden">
                  <button type="button" onClick={() => setDetailId(w.id)} className="w-full flex items-center justify-between py-3 px-4 text-left hover:bg-slate-800/40" data-testid={`website-card-${w.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Server className="w-5 h-5 text-indigo-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-200">{w.site_name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {w.site_url} · {ownerEmail} · Created {formatDay(w.created_at)}
                          {w.content_type ? ` · ${w.content_type}` : ''}
                          {(w.sister_count || 0) > 0 ? ` · ${w.sister_count} sister site${w.sister_count === 1 ? '' : 's'}` : ''}
                          {w.attribution?.label ? ` · ${w.attribution.label}` : ''}
                          {` · EasyPeazy sign-in ${ownerSignIn ? formatDay(ownerSignIn) : 'never'}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {w.woocommerce_enabled && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">Store</span>}
                      {w.content_policy && <span className="text-xs bg-rose-500/20 text-rose-300 px-2 py-0.5 rounded">{w.content_policy}</span>}
                      <StatusBadge status={w.status} />
                    </div>
                  </button>
                  <div className="px-4 pb-2">
                    <InviteClaimChips website={w} />
                  </div>
                  {w.provisioning_steps && (
                    <div className="px-4 pb-1">
                      <div className="flex gap-1">
                        {w.provisioning_steps.map((s, i) => (
                          <div key={i} className={`h-1.5 flex-1 rounded-full ${s.status === 'completed' ? 'bg-emerald-500' : s.status === 'in_progress' ? 'bg-amber-500 animate-pulse' : s.status === 'failed' ? 'bg-red-500' : 'bg-slate-700'}`} title={`${s.label}: ${s.status}`} />
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="px-4 pb-3 flex gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                    <WebsiteReadyActions
                      website={w}
                      testIdPrefix={`access-site-${w.id}`}
                      openTestId={`access-site-${w.id}`}
                      editTestId={`edit-website-${w.id}`}
                    />
                    {(w.status === 'error' || w.status === 'failed') && (
                      <button type="button" onClick={() => handleReprovision(w)} className="inline-flex items-center gap-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-all" data-testid={`reprovision-${w.id}`}>
                        <RefreshCw className="w-3.5 h-3.5" /> Re-provision
                      </button>
                    )}
                    <form onSubmit={(e) => { e.preventDefault(); startTransfer(w); }} className="flex items-center gap-1.5 flex-wrap" data-testid={`transfer-form-${w.id}`}>
                      <input
                        type="email"
                        value={transferEmail[w.id] || ''}
                        onChange={e => setTransferEmail(m => ({ ...m, [w.id]: e.target.value }))}
                        placeholder="New owner email"
                        className="bg-slate-900 border border-slate-700 rounded-lg h-8 px-2 text-xs text-slate-100 placeholder:text-slate-500 outline-none w-48"
                        data-testid={`transfer-email-${w.id}`}
                      />
                      <button type="submit" disabled={transferBusy === w.id} className="inline-flex items-center gap-1.5 bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50" data-testid={`transfer-owner-${w.id}`}>
                        <ArrowRightLeft className="w-3.5 h-3.5" /> {transferBusy === w.id ? 'Transferring…' : 'Transfer to this email'}
                      </button>
                    </form>
                    <p className="w-full text-[11px] text-slate-500">Transfer confirms this website and email, then previews the invite. Resend (current owner only) is on the site detail.</p>
                    <div className="w-full"><WebsitePeople website={w} canManage /></div>
                  </div>
                  {actionNote[w.id] && (
                    <p className={`px-4 pb-3 text-xs ${actionNote[w.id].kind === 'error' ? 'text-red-400' : 'text-emerald-400'}`} data-testid={`website-action-note-${w.id}`}>{actionNote[w.id].text || actionNote[w.id]}</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : <p className="text-slate-500 text-sm">{filterKey === 'all' ? 'No websites created yet.' : 'No websites match this filter.'}</p>}
      </div>

      <div id="reserved-labels" className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 mb-6" data-testid="reserved-labels">
        <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Reserved URLs ({reservedLabels.length})</h3>
        <p className="text-xs text-slate-500 mb-4">Hold {`{label}`}.{EASY_PEAZY_SUBDOMAIN_BASE} so Bring Over and claim cannot steal it. The list below is every hold. Release URL when the name should be free again.</p>
        <form onSubmit={handleReserve} className="flex flex-wrap gap-2 mb-4 items-end">
          <input required value={reserveForm.label} onChange={e => setReserveForm(f => ({ ...f, label: e.target.value }))} placeholder="label" className="bg-slate-900 border border-slate-700 rounded-lg h-9 px-3 text-sm text-white placeholder-slate-600" data-testid="reserve-label-input" />
          <select value={reserveForm.base} onChange={e => setReserveForm(f => ({ ...f, base: e.target.value }))} className="bg-slate-900 border border-slate-700 rounded-lg h-9 px-2 text-sm text-white" data-testid="reserve-base">
            <option value={EASY_PEAZY_SUBDOMAIN_BASE}>{EASY_PEAZY_SUBDOMAIN_BASE}</option>
            <option value={CHURCHCONNECT_SUBDOMAIN_BASE}>{CHURCHCONNECT_SUBDOMAIN_BASE}</option>
          </select>
          <input value={reserveForm.note} onChange={e => setReserveForm(f => ({ ...f, note: e.target.value }))} placeholder="Note (optional)" className="bg-slate-900 border border-slate-700 rounded-lg h-9 px-3 text-sm text-white placeholder-slate-600" data-testid="reserve-note" />
          <button type="submit" disabled={reserveBusy} className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white px-3 h-9 rounded-lg text-xs font-medium" data-testid="reserve-label-submit">
            {reserveBusy ? 'Holding…' : 'Reserve URL'}
          </button>
        </form>
        {reserveError && <p className="text-xs text-red-400 mb-3">{reserveError}</p>}
        {reservedLabels.length > 0 ? (
          <div className="space-y-2">
            {reservedLabels.map(d => (
              <div key={d.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/30 border border-slate-800" data-testid={`reserved-label-${d.id}`}>
                <div>
                  <p className="text-sm text-slate-200">{d.domain_name}</p>
                  <p className="text-xs text-slate-500">{d.contact_info?.note || 'Held so it cannot be claimed'}</p>
                </div>
                <button type="button" onClick={() => handleRelease(d)} className="text-xs text-slate-400 hover:text-red-400" data-testid={`release-label-${d.id}`}>Release URL</button>
              </div>
            ))}
          </div>
        ) : <p className="text-slate-500 text-sm">No URLs held yet.</p>}
      </div>

      <div id="tracked-domains" className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>All Domains ({domains.length})</h3>
          <button onClick={() => { setAdding(a => !a); setDomainError(''); }} className="inline-flex items-center gap-1.5 bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-all" data-testid="add-domain-toggle">
            <Plus className="w-3.5 h-3.5" /> {adding ? 'Cancel' : 'Track a domain'}
          </button>
        </div>

        {adding && (
          <form onSubmit={addDomain} className="mb-4 rounded-lg bg-slate-800/40 border border-slate-700 p-4 space-y-3" data-testid="add-domain-form">
            <p className="text-xs text-slate-400">
              For domains held somewhere else. This records the renewal date so you can see what's
              coming up — it doesn't change anything at the registrar.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required value={domainForm.domain_name} onChange={e => setDomainForm(f => ({ ...f, domain_name: e.target.value }))} placeholder="example.com" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-600" data-testid="add-domain-name" />
              <select value={domainForm.registrar} onChange={e => setDomainForm(f => ({ ...f, registrar: e.target.value }))} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white" data-testid="add-domain-registrar">
                <option value="enom">Enom</option>
                <option value="dreamhost">DreamHost</option>
                <option value="godaddy">GoDaddy</option>
                <option value="namecheap">Namecheap</option>
                <option value="other">Other</option>
              </select>
              <label className="text-xs text-slate-500">
                Renews on
                <input type="date" value={domainForm.expiration_date} onChange={e => setDomainForm(f => ({ ...f, expiration_date: e.target.value }))} className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white" data-testid="add-domain-expiry" />
              </label>
              <label className="text-xs text-slate-500">
                Renewal cost
                <input type="number" step="0.01" min="0" value={domainForm.renew_cost} onChange={e => setDomainForm(f => ({ ...f, renew_cost: e.target.value }))} placeholder="17.99" className="mt-1 w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-600" data-testid="add-domain-cost" />
              </label>
            </div>
            {domainError && <p className="text-xs text-red-400">{domainError}</p>}
            <button type="submit" disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all" data-testid="add-domain-submit">
              {saving ? 'Adding…' : 'Add to the register'}
            </button>
          </form>
        )}

        {domains.length > 0 ? (
          <div className="space-y-2">
            {domains.map(d => {
              const client = getClient(d.client_id);
              const site = getWebsite(d.website_id);
              const tracked = !MANAGED_REGISTRARS.includes((d.registrar || '').toLowerCase());
              return (
                <div key={d.id} className="flex items-center justify-between py-3 px-4 rounded-lg bg-slate-800/30 border border-slate-800" data-testid={`admin-domain-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Globe className="w-5 h-5 text-cyan-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-200 truncate">{d.domain_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {d.registrar || 'unknown registrar'}
                        {d.renew_cost != null && Number(d.renew_cost) > 0 && ` · $${Number(d.renew_cost).toFixed(2)}/yr`}
                        {d.renewal_status === 'due' && <span className={new Date(d.renewal_due_at) < new Date() ? 'text-red-400 font-medium' : 'text-amber-400'} data-testid={`admin-renewal-flag-${d.id}`}>{` · unpaid, renews on ${d.renewal_due_at ? new Date(d.renewal_due_at).toLocaleDateString() : '?'}${d.renewal_price ? ` ($${d.renewal_price}/yr)` : ''}`}</span>}
                        {d.renewal_status === 'subscribed' && ' · kept by customer (on website plan)'}
                        {d.renewal_status === 'due' && d.renewal_flagged_at && ` · removed on ${new Date(new Date(d.renewal_flagged_at).getTime() + 90 * 86400000).toLocaleDateString()} if no choice`}
                        {d.renewal_status === 'released_needs_admin' && <span className="text-red-400">{' · free address chosen: automatic move or auto-renew off failed, check this domain'}</span>}
                        {d.renewal_status === 'deleted' && ' · site removed after 90 days (domain not renewed)'}
                        {d.renewal_status === 'delete_failed' && <span className="text-red-400">{' · 90-day removal had errors, see audit log'}</span>}
                        {d.renewal_status === 'released' && <span className="text-amber-400" data-testid={`admin-renewal-released-${d.id}`}>{' · customer chose a free address: site moved, domain not renewing'}</span>}
                        {site ? ` · ${site.site_name}` : ' · not pointed at a site'}
                        {client?.full_name && ` · ${client.full_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <ExpiryNote value={d.expiration_date} />
                    <StatusBadge status={d.status} />
                    {tracked && (
                      <button onClick={() => removeDomain(d)} title="Stop tracking" className="text-slate-600 hover:text-red-400 transition-colors" data-testid={`remove-domain-${d.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : <p className="text-slate-500 text-sm">No domains registered yet.</p>}
      </div>
    </div>
  );
}


function AdminEmailTasks({ openTaskId, onOpenTask, onClearTask }) {
  const [data, setData] = useState({ pending: [], all: [], pending_count: 0, attention: null });
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [staffEmail, setStaffEmail] = useState('');
  const [staffEmailSaved, setStaffEmailSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const [emailRes, staffRes] = await Promise.all([
        api.getAdminEmailTasks(),
        api.getEmailStaff()
      ]);
      setData(emailRes.data);
      setStaffEmail(staffRes.data.staff_email || '');
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tasks = [...(data.pending || []), ...(data.all || [])];
  const selected = openTaskId ? tasks.find(email => email.id === openTaskId) : null;
  const attention = data.attention && data.attention.id
    ? data.attention
    : (data.pending || []).find(email => email.id && email.admin_action_required) || null;

  useEffect(() => {
    if (loading || !openTaskId || selected) return;
    onClearTask?.();
  }, [loading, openTaskId, selected, onClearTask]);

  const saveStaffEmail = async () => {
    try {
      await api.setEmailStaff(staffEmail.trim());
      setStaffEmailSaved(true);
      setTimeout(() => setStaffEmailSaved(false), 2000);
    } catch (err) { console.error(err); }
  };

  const markProvisioned = async (emailId) => {
    setProvisioning(emailId);
    try {
      await api.provisionEmail(emailId);
      onClearTask?.();
      load();
    } catch (err) { console.error(err); }
    finally { setProvisioning(null); }
  };

  const displayList = showAll ? data.all : data.pending;

  return (
    <div className="space-y-6" data-testid="admin-email-tasks">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Email Provisioning</h2>
          <p className="text-sm text-slate-400 mt-1">Hand-create paid custom-domain mailboxes in <a href="https://www.spaceship.com/application/email" target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline">Spaceship Spacemail standalone</a> (not hosting — EasyPeazy sites are not on Spaceship hosting), then mark them as done here.</p>
        </div>
        <div className="flex items-center gap-3">
          {data.pending_count > 0 && (
            <span className="bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2" data-testid="pending-email-count">
              <AlertCircle className="w-4 h-4" /> {data.pending_count} pending
            </span>
          )}
          <div className="flex bg-slate-800 rounded-lg overflow-hidden border border-slate-700">
            <button onClick={() => setShowAll(false)} className={`px-3 py-1.5 text-xs transition-colors ${!showAll ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400'}`} data-testid="show-pending-btn">Needs Action</button>
            <button onClick={() => setShowAll(true)} className={`px-3 py-1.5 text-xs transition-colors ${showAll ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400'}`} data-testid="show-all-emails-btn">All Emails</button>
          </div>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(open) => { if (!open) onClearTask?.(); }}>
        <SheetContent side="right" className="bg-slate-950 border-slate-800 text-slate-100 sm:max-w-md overflow-y-auto" data-testid="email-task-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-white">{selected.email_address || 'Mailbox order'}</SheetTitle>
                <SheetDescription className="text-slate-400">
                  Existing Email Tasks door. Hand-create this mailbox in Spacemail standalone, then mark it done.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="flex flex-wrap gap-1.5">
                  <StatusChip tone={selected.admin_action_required ? 'warn' : selected.status === 'active' ? 'ok' : 'slate'}>
                    {selected.admin_action_required ? 'Needs action' : (selected.status || 'unknown')}
                  </StatusChip>
                  {selected.client_name && <StatusChip tone="info">{selected.client_name}</StatusChip>}
                  {selected.domain_name && <StatusChip>{selected.domain_name}</StatusChip>}
                </div>
                <div>
                  <p className="text-slate-200 break-all">{selected.email_address}</p>
                  <p className="text-xs text-slate-500 mt-1">{selected.client_name || 'Customer'}{selected.domain_name ? ` · ${selected.domain_name}` : ''}</p>
                  {selected.created_at && <p className="text-xs text-slate-500">Ordered {new Date(selected.created_at).toLocaleDateString()}</p>}
                  {selected.auto_provision_error && <p className="text-xs text-rose-300 mt-2">{selected.auto_provision_error}</p>}
                </div>
                {selected.admin_action_required && (
                  <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside" data-testid="email-task-checklist">
                    <li>Open <a href="https://www.spaceship.com/application/email" target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline">Spacemail standalone</a> — not hosting</li>
                    <li>Create {selected.email_address || 'the mailbox'} within a couple of days</li>
                    <li>Come back here and click Mark as Done</li>
                  </ol>
                )}
                <a href="https://www.spaceship.com/application/email" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-400 px-3 py-1.5 rounded-lg text-xs font-medium">
                  <ExternalLink className="w-3.5 h-3.5" /> Open Spacemail standalone
                </a>
                {selected.admin_action_required && (
                  <button
                    type="button"
                    onClick={() => markProvisioned(selected.id)}
                    disabled={provisioning === selected.id}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-medium transition-all disabled:opacity-50 flex items-center gap-2"
                    data-testid={`drawer-provision-email-${selected.id}`}
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    {provisioning === selected.id ? 'Updating...' : 'Mark as Done'}
                  </button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {data.pending_count > 0 && !showAll && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
          <h4 className="text-sm font-medium text-amber-400 mb-2">How to provision:</h4>
          <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside">
            <li>Open <a href="https://www.spaceship.com/application/email" target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline">Spacemail standalone</a> — not the website hosting panel</li>
            <li>Create the mailbox shown below within a couple of days</li>
            <li>Come back here and click "Mark as Done"</li>
          </ol>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading...</div>
      ) : displayList.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-slate-800 bg-slate-900/40" data-testid="email-tasks-empty">
          <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-slate-400">{showAll ? 'No email accounts yet.' : 'All caught up! No pending email tasks.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayList.map(email => (
            <div key={email.id} className={`flex items-center justify-between p-4 rounded-xl border ${email.admin_action_required ? 'border-amber-500/20 bg-amber-500/5' : 'border-slate-800 bg-slate-900/40'}`} data-testid={`email-task-${email.id}`}>
              <button
                type="button"
                onClick={() => email.id && onOpenTask?.(email.id)}
                className="flex items-center gap-4 min-w-0 text-left flex-1"
                data-testid={email.admin_action_required ? `open-email-task-${email.id}` : `open-email-row-${email.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${email.admin_action_required ? 'bg-amber-500/20' : email.status === 'active' ? 'bg-emerald-500/20' : 'bg-slate-800'}`}>
                  <Mail className={`w-5 h-5 ${email.admin_action_required ? 'text-amber-400' : email.status === 'active' ? 'text-emerald-400' : 'text-slate-500'}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">{email.email_address}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-500">{email.client_name || 'Customer'}</span>
                    {email.domain_name && <span className="text-xs text-slate-600">• {email.domain_name}</span>}
                    <span className="text-xs text-slate-600">• {email.created_at ? new Date(email.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
              </button>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                {email.status === 'active' && (
                  <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg">
                    <CheckCircle className="w-3 h-3" /> Active
                  </span>
                )}
                {email.status === 'pending_payment' && (
                  <span className="flex items-center gap-1.5 text-xs text-slate-400 bg-slate-800 px-2.5 py-1 rounded-lg">
                    <Clock className="w-3 h-3" /> Awaiting Payment
                  </span>
                )}
                {email.admin_action_required && (
                  <button
                    type="button"
                    onClick={() => markProvisioned(email.id)}
                    disabled={provisioning === email.id}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-medium transition-all disabled:opacity-50 flex items-center gap-2"
                    data-testid={`provision-email-${email.id}`}
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    {provisioning === email.id ? 'Updating...' : 'Mark as Done'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5" data-testid="email-task-notifications">
        <h4 className="text-sm font-medium text-white mb-2">Email Task Notifications</h4>
        <p className="text-xs text-slate-400 mb-2">Paid mailbox orders send a loud transactional staff email (subject starts with ACTION NEEDED). Same existing notify path — no new product.</p>
        <p className="text-xs text-amber-300/90 mb-3" data-testid="staff-email-empty-hint">Staff notify starts empty. Save a staff email once — <span className="text-slate-200">lincoln@unitedundergod.org</span> — or mailbox-order alerts only go to the admin account.</p>
        <div className="flex gap-2">
          <input value={staffEmail} onChange={e => { setStaffEmail(e.target.value); setStaffEmailSaved(false); }} className="flex-1 bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none placeholder:text-slate-400" placeholder="lincoln@unitedundergod.org" data-testid="staff-email-input" />
          <button onClick={saveStaffEmail} className={`px-5 h-10 rounded-lg text-sm font-medium transition-all ${staffEmailSaved ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-indigo-500 hover:bg-indigo-600 text-white'}`} data-testid="save-staff-email-btn">
            {staffEmailSaved ? 'Saved!' : 'Save'}
          </button>
        </div>
        {staffEmail ? (
          <p className="text-xs text-slate-500 mt-2">Both you and <span className="text-slate-300">{staffEmail}</span> will receive mailbox-order staff notifications.</p>
        ) : (
          <p className="text-xs text-slate-500 mt-2">Nothing is saved yet. Click Save after entering the staff address.</p>
        )}
        <details className="mt-4 rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="staff-notify-subject-preview">
          <summary className="text-xs text-slate-400 cursor-pointer">What the staff email looks like</summary>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-3 mb-1" data-testid="staff-notify-sample-label">Sample subject / not a live task</p>
          <p className="text-sm text-slate-300">Sample: ACTION NEEDED — EasyPeazy mailbox order: {'{mailbox}@{domain}'}</p>
          <p className="text-xs text-slate-500 mt-2">This is a template of the staff email subject. It is not a mailbox order. Live orders appear above under Needs Action.</p>
          <p className="text-xs text-slate-500 mt-2">Body includes customer name/email, domain, plan tier when known (Pro 1 / Business 3 / Advanced 5), mailbox address(es), paid amount when available, and the standalone Spacemail hand-create instruction.</p>
        </details>
        {!attention && (
          <p className="sr-only" data-testid="email-task-no-attention">No live mailbox-order task needs attention.</p>
        )}
      </div>
    </div>
  );
}

function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    api.getTickets().then(r => setTickets(r.data.tickets || [])).catch(console.error);
  }, []);

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const res = await api.getTicketMessages(ticket.id);
      setMessages(res.data.messages || []);
    } catch (err) { console.error(err); }
  };

  const sendReply = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    try {
      await api.sendTicketMessage(selectedTicket.id, newMessage);
      const res = await api.getTicketMessages(selectedTicket.id);
      setMessages(res.data.messages || []);
      setNewMessage('');
      const tRes = await api.getTickets();
      setTickets(tRes.data.tickets || []);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="animate-fade-in" data-testid="admin-support">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Support Tickets</h2>

      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="admin-ticket-modal">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{selectedTicket.subject}</h3>
                <p className="text-xs text-slate-500">#{selectedTicket.id?.slice(0, 8)} | {selectedTicket.category} | {selectedTicket.priority}</p>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3 mb-4 min-h-[200px]">
              {messages.map(m => (
                <div key={m.id} className={`p-3 rounded-lg ${m.sender_type === 'admin' ? 'bg-indigo-500/10 border border-indigo-500/20 ml-8' : 'bg-slate-800/50 border border-slate-700 mr-8'}`}>
                  <p className="text-xs text-slate-500 mb-1">{m.sender_type === 'admin' ? 'Admin' : 'Customer'} - {new Date(m.created_at).toLocaleString()}</p>
                  <p className="text-sm text-slate-300">{m.message}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendReply()} className="flex-1 bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-11 px-4 text-slate-100 outline-none" placeholder="Reply to customer..." data-testid="admin-reply-input" />
              <button onClick={sendReply} className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 rounded-lg transition-all" data-testid="admin-send-reply-btn"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}

      {tickets.length > 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/60">
                <th className="text-left py-3 px-6 text-slate-400 font-medium">Subject</th>
                <th className="text-left py-3 px-6 text-slate-400 font-medium">Category</th>
                <th className="text-left py-3 px-6 text-slate-400 font-medium">Priority</th>
                <th className="text-left py-3 px-6 text-slate-400 font-medium">Status</th>
                <th className="text-left py-3 px-6 text-slate-400 font-medium">Date</th>
                <th className="text-right py-3 px-6 text-slate-400 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                  <td className="py-3 px-6 text-slate-200">{t.subject}</td>
                  <td className="py-3 px-6 text-slate-400 capitalize">{t.category}</td>
                  <td className="py-3 px-6">
                    <span className={`text-xs px-2 py-0.5 rounded ${t.priority === 'high' ? 'bg-red-500/20 text-red-400' : t.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'}`}>{t.priority}</span>
                  </td>
                  <td className="py-3 px-6"><StatusBadge status={t.status} /></td>
                  <td className="py-3 px-6 text-slate-500 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-6 text-right">
                    <button onClick={() => openTicket(t)} className="text-indigo-400 hover:text-indigo-300 text-xs" data-testid={`view-ticket-${t.id?.slice(0, 8)}`}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-12 text-center">
          <Headphones className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No support tickets.</p>
        </div>
      )}
    </div>
  );
}

function AdminPayments() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.getAdminPayments().then(r => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return <div className="text-slate-500">Loading...</div>;

  const getClient = (cid) => data.clients?.find(c => c.id === cid);

  return (
    <div className="animate-fade-in" data-testid="admin-payments">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Payment Tracking</h2>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Date</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Customer</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Type</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Amount</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Method</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.payments?.length > 0 ? data.payments.map(p => {
              const client = getClient(p.client_id);
              return (
                <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                  <td className="py-3 px-6 text-slate-300">{new Date(p.payment_date || p.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-6 text-slate-200">{client?.full_name || 'Unknown'}</td>
                  <td className="py-3 px-6 text-slate-400 capitalize">{p.payment_type}</td>
                  <td className="py-3 px-6 text-emerald-400 font-medium">${p.amount}</td>
                  <td className="py-3 px-6 text-slate-400 capitalize">{p.payment_method}</td>
                  <td className="py-3 px-6"><StatusBadge status={p.status} /></td>
                </tr>
              );
            }) : (
              <tr><td colSpan={6} className="py-8 text-center text-slate-500">No payments recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    api.getAdminAnalytics(period).then(r => setData(r.data)).catch(console.error);
  }, [period]);

  if (!data) return <div className="text-slate-500">Loading...</div>;

  const metrics = [
    { label: 'Total Revenue', value: `$${data.total_revenue}`, icon: DollarSign, color: 'text-emerald-400' },
    { label: 'Customer Growth', value: `${data.customer_growth}%`, icon: TrendingUp, color: 'text-indigo-400' },
    { label: 'New Websites', value: data.total_websites, icon: Server, color: 'text-cyan-400' },
    { label: 'Avg Revenue/Customer', value: `$${data.avg_revenue_per_customer}`, icon: Users, color: 'text-purple-400' },
    // storage_used_mb is inserted 0 and never collected. Do not show a GB figure.
    { label: 'Storage Used', value: 'Not metered', icon: HardDrive, color: 'text-teal-400' },
    { label: 'Churn Rate', value: `${data.churn_rate}%`, icon: TrendingUp, color: 'text-amber-400' },
  ];

  return (
    <div className="animate-fade-in" data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Analytics</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setPeriod(d)} className={`px-4 py-2 rounded-lg text-sm transition-colors ${period === d ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800 text-slate-400 hover:text-white'}`} data-testid={`period-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {metrics.map((m, i) => (
          <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6" data-testid={`metric-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <m.icon className={`w-4 h-4 ${m.color}`} />
              <p className="text-sm text-slate-500">{m.label}</p>
            </div>
            <p className={`text-2xl font-bold ${m.color}`} style={{ fontFamily: 'Outfit' }}>{m.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


const COUPON_COVER_OPTIONS = [
  { id: 'website', label: 'Website', hint: 'Hosting, the free web address and publishing — no $12/yr plan.' },
  { id: 'domain', label: 'Custom domain', hint: 'Registers a domain of their choice at $0. You pay the registrar.' },
];

function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [maxUses, setMaxUses] = useState(100);
  // What redeeming the coupon unlocks on the customer's account.
  const [covers, setCovers] = useState(['website']);
  const toggleCover = (key) => setCovers(c => c.includes(key) ? c.filter(k => k !== key) : [...c, key]);

  const fetchCoupons = () => {
    api.getAdminCoupons().then(r => setCoupons(r.data.coupons || [])).catch(console.error);
  };
  useEffect(() => { fetchCoupons(); }, []);

  const createCoupon = async (e) => {
    e.preventDefault();
    if (!newCode.trim()) return;
    if (!covers.length) { alert('Choose what the coupon covers.'); return; }
    try {
      await api.createCoupon({ code: newCode.trim(), max_uses: maxUses, active: true, covers });
      setNewCode(''); setMaxUses(100); setCovers(['website']); setShowCreate(false);
      fetchCoupons();
    } catch (err) { alert(err.response?.data?.detail || 'Error creating coupon'); }
  };

  const toggleCoupon = async (id) => {
    try { await api.toggleCoupon(id); fetchCoupons(); } catch (err) { console.error(err); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try { await api.deleteCoupon(id); fetchCoupons(); } catch (err) { console.error(err); }
  };

  return (
    <div className="animate-fade-in" data-testid="admin-coupons-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Coupons</h2>
          <p className="text-sm text-slate-400 mt-1">Create coupon codes that give select people their website or domain free</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm transition-all" data-testid="create-coupon-btn"><Plus className="w-4 h-4" /> Create Coupon</button>
      </div>

      {showCreate && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 mb-6">
          <form onSubmit={createCoupon} className="flex flex-wrap items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs text-slate-400 mb-1">Coupon Code</label>
              <input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())} className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none font-mono" placeholder="FRIENDS2026" required data-testid="coupon-code-input" />
            </div>
            <div className="w-32">
              <label className="block text-xs text-slate-400 mb-1">Max Uses</label>
              <input type="number" value={maxUses} onChange={e => setMaxUses(parseInt(e.target.value) || 1)} min="1" className="w-full bg-slate-800/50 border border-slate-700 focus:border-indigo-500 rounded-lg h-10 px-3 text-sm text-slate-100 outline-none" data-testid="coupon-max-uses" />
            </div>
            <button type="submit" className="bg-emerald-500 hover:bg-emerald-600 text-white h-10 px-5 rounded-lg text-sm font-medium transition-all" data-testid="save-coupon-btn">Create</button>
            <button type="button" onClick={() => setShowCreate(false)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 h-10 px-4 rounded-lg text-sm transition-all border border-slate-700">Cancel</button>
          </form>
          <fieldset className="mt-4" data-testid="coupon-covers">
            <legend className="block text-xs text-slate-400 mb-2">What it covers</legend>
            <div className="flex flex-wrap gap-4">
              {COUPON_COVER_OPTIONS.map(o => (
                <label key={o.id} className="flex items-start gap-2 cursor-pointer max-w-xs">
                  <input type="checkbox" checked={covers.includes(o.id)} onChange={() => toggleCover(o.id)} className="w-4 h-4 mt-0.5 rounded border-slate-600 text-indigo-500 focus:ring-indigo-500 bg-slate-800" data-testid={`coupon-cover-${o.id}`} />
                  <span>
                    <span className="block text-sm text-slate-200">{o.label}</span>
                    <span className="block text-xs text-slate-500">{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <p className="text-xs text-slate-500 mt-3">A customer enters the code when they sign up, or later from their portal. It applies to their whole account.</p>
        </div>
      )}

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        {coupons.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-3 px-5 text-slate-400 font-medium">Code</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Uses</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Created</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Last Used</th>
                <th className="text-right py-3 px-5 text-slate-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => (
                <tr key={c.id} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                  <td className="py-3 px-5"><span className="font-mono text-white font-medium">{c.code}</span></td>
                  <td className="py-3 px-4 text-slate-300">{(c.covers?.length ? c.covers : ['website']).map(k => COUPON_COVER_OPTIONS.find(o => o.id === k)?.label || k).join(' + ')}</td>
                  <td className="py-3 px-4">
                    {c.active ? <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">Active</span> : <span className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded">Inactive</span>}
                  </td>
                  <td className="py-3 px-4 text-slate-300">{c.times_used} / {c.max_uses}</td>
                  <td className="py-3 px-4 text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                  <td className="py-3 px-4 text-slate-400">{c.last_used_at ? new Date(c.last_used_at).toLocaleDateString() : 'Never'}</td>
                  <td className="py-3 px-5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => toggleCoupon(c.id)} className="text-slate-400 hover:text-indigo-400 transition-colors" title={c.active ? 'Deactivate' : 'Activate'} data-testid={`toggle-coupon-${c.id}`}>
                        {c.active ? <ToggleRight className="w-5 h-5 text-emerald-400" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <button onClick={() => deleteCoupon(c.id)} className="text-slate-400 hover:text-red-400 transition-colors" title="Delete" data-testid={`delete-coupon-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <Tag className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500">No coupons created yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}


function AdminReferrals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.getAdminReferrals().then(r => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="animate-fade-in" data-testid="admin-referrals">
      <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Referral Program</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
            <Gift className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Total Referrals</p>
            <p className="text-2xl font-bold text-indigo-400" style={{ fontFamily: 'Outfit' }}>{data.total_referrals}</p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Total Credits Issued</p>
            <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: 'Outfit' }}>${data.total_credits_issued?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Conversion Rate</p>
            <p className="text-2xl font-bold text-cyan-400" style={{ fontFamily: 'Outfit' }}>{data.total_referrals > 0 ? '100%' : '0%'}</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Referrer</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Referred User</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Reward</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Status</th>
              <th className="text-left py-3 px-6 text-slate-400 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {data.referrals?.length > 0 ? data.referrals.map((r, i) => (
              <tr key={r.id || i} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                <td className="py-3 px-6">
                  <p className="text-slate-200">{r.referrer_name}</p>
                  <p className="text-xs text-slate-500">{r.referrer_email}</p>
                </td>
                <td className="py-3 px-6">
                  <p className="text-slate-200">{r.referred_name}</p>
                  <p className="text-xs text-slate-500">{r.referred_email}</p>
                </td>
                <td className="py-3 px-6 text-emerald-400 font-medium">${r.referrer_reward} each</td>
                <td className="py-3 px-6"><StatusBadge status={r.status || 'completed'} /></td>
                <td className="py-3 px-6 text-slate-500 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
              </tr>
            )) : (
              <tr><td colSpan={5} className="py-8 text-center text-slate-500">No referrals yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const INTEGRATION_HEALTH_KEY = {
  stripe: 'stripe',
  spaceship: 'spaceship',
  builder: 'builder',
  resend: 'resend',
};

function worksBadge(status) {
  if (!status) return { label: 'Checking…', tone: 'bg-slate-700 text-slate-400' };
  if (status === 'ok') return { label: 'Works', tone: 'bg-emerald-500/20 text-emerald-400' };
  if (status === 'missing') return { label: 'Missing', tone: 'bg-slate-700 text-slate-400' };
  if (status === 'unauthorized') return { label: 'Rejected', tone: 'bg-amber-500/20 text-amber-400' };
  return { label: 'Error', tone: 'bg-rose-500/20 text-rose-300' };
}

function AdminSettings() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState('');

  useEffect(() => {
    api.getIntegrations()
      .then(r => setData(r.data))
      .catch(() => setError ('Could not read the integration status.'));
    api.getHealthDeep()
      .then(r => setHealth(r.data))
      .catch(() => setHealthError('Deep health check unavailable.'));
  }, []);

  if (error) return <div className="text-red-400 text-sm">{error}</div>;
  if (!data) return <div className="text-slate-500">Loading...</div>;

  const blocking = data.integrations.filter(i => i.blocks_selling && !i.configured);
  const credentials = health?.credentials || {};

  return (
    <div className="animate-fade-in" data-testid="admin-settings">
      <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Integrations</h2>
      <p className="text-sm text-slate-500 mb-6">
        Read from what the app actually uses. Keys are set as {data.managed_in} — they cannot be
        entered here, and a page that let you type one would only look like it had worked.
      </p>

      {blocking.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-6" data-testid="integrations-blocking">
          <p className="text-sm text-amber-300">
            {blocking.length === 1
              ? `${blocking[0].name} is not configured, so a customer cannot complete a purchase.`
              : `${blocking.length} integrations are not configured, so a customer cannot complete a purchase.`}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="space-y-3">
          {data.integrations.map(i => {
            const healthKey = INTEGRATION_HEALTH_KEY[i.id];
            const deepStatus = healthKey ? credentials[healthKey] : null;
            const works = healthKey ? worksBadge(healthError ? 'error' : deepStatus) : null;
            return (
            <div key={i.id} className="flex items-start justify-between gap-4 py-3 px-4 rounded-lg bg-slate-800/30 border border-slate-800" data-testid={`integration-${i.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-200">{i.name}</p>
                <p className="text-xs text-slate-500">{i.purpose}</p>
                <p className="text-[11px] text-slate-600 font-mono mt-1 break-all">{i.env.join(', ')}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {i.configured ? (
                  <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded" data-testid={`integration-set-${i.id}`}>
                    <CheckCircle className="w-3.5 h-3.5" /> Set
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${i.blocks_selling ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'}`} data-testid={`integration-set-${i.id}`}>
                    <AlertCircle className="w-3.5 h-3.5" /> Not set
                  </span>
                )}
                {works && (
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${works.tone}`} data-testid={`integration-works-${i.id}`}>
                    {deepStatus === 'ok' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                    {works.label}
                  </span>
                )}
              </div>
            </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-600 mt-4">
          "Set" means the value is present. "Works" is GET /api/health?deep=1
          {health?.cached ? ' (cached)' : ''} — the live call. Stripe webhook stays Set / Not set; there is no deep probe for it.
        </p>
        {healthError && <p className="text-xs text-amber-300 mt-2" data-testid="integrations-health-error">{healthError}</p>}
      </div>
    </div>
  );
}
